/*!
 * @jscrypto/ciphers v0.12.0
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */
import { assertBytes, equalBytes as equalBytes$1, concatBytes } from '@jscrypto/core';

/**
 * Utilities for hex, bytes, CSPRNG.
 * @module
 */
/*! noble-ciphers - MIT License (c) 2023 Paul Miller (paulmillr.com) */
/**
 * Checks if something is Uint8Array. Be careful: nodejs Buffer will return true.
 * @param a - Value to inspect.
 * @returns `true` when the value is a Uint8Array view, including Node's `Buffer`.
 * @example
 * Guards a value before treating it as raw key material.
 *
 * ```ts
 * isBytes(new Uint8Array());
 * ```
 */
function isBytes(a) {
    // Plain `instanceof Uint8Array` is too strict for some Buffer / proxy /
    // cross-realm cases. The fallback still requires a real ArrayBuffer view
    // so plain JSON-deserialized `{ constructor: ... }`
    // spoofing is rejected, and `BYTES_PER_ELEMENT === 1` keeps the fallback on byte-oriented views.
    return (a instanceof Uint8Array ||
        (ArrayBuffer.isView(a) &&
            a.constructor.name === 'Uint8Array' &&
            'BYTES_PER_ELEMENT' in a &&
            a.BYTES_PER_ELEMENT === 1));
}
/**
 * Asserts something is boolean.
 * @param b - Value to validate.
 * @throws On wrong argument types. {@link TypeError}
 * @example
 * Validates a boolean option before branching on it.
 *
 * ```ts
 * abool(true);
 * ```
 */
function abool(b) {
    if (typeof b !== 'boolean')
        throw new TypeError(`boolean expected, not ${b}`);
}
/**
 * Asserts something is a non-negative safe integer.
 * @param n - Value to validate.
 * @throws On wrong argument types. {@link TypeError}
 * @throws On wrong argument ranges or values. {@link RangeError}
 * @example
 * Validates a non-negative length or counter.
 *
 * ```ts
 * anumber(1);
 * ```
 */
function anumber(n) {
    if (typeof n !== 'number')
        throw new TypeError('number expected, got ' + typeof n);
    if (!Number.isSafeInteger(n) || n < 0)
        throw new RangeError('positive integer expected, got ' + n);
}
/**
 * Asserts something is Uint8Array.
 * @param value - Value to validate.
 * @param length - Expected byte length.
 * @param title - Optional label used in error messages.
 * @returns The validated byte array.
 * On Node, `Buffer` is accepted too because it is a Uint8Array view.
 * @throws On wrong argument types. {@link TypeError}
 * @throws On wrong argument lengths. {@link RangeError}
 * @example
 * Validates a fixed-length nonce or key buffer.
 *
 * ```ts
 * abytes(new Uint8Array([1, 2]), 2);
 * ```
 */
function abytes(value, length, title = '') {
    const bytes = isBytes(value);
    const len = value?.length;
    const needsLen = length !== undefined;
    if (!bytes || (needsLen && len !== length)) {
        const prefix = title && `"${title}" `;
        const ofLen = needsLen ? ` of length ${length}` : '';
        const got = bytes ? `length=${len}` : `type=${typeof value}`;
        const message = prefix + 'expected Uint8Array' + ofLen + ', got ' + got;
        if (!bytes)
            throw new TypeError(message);
        throw new RangeError(message);
    }
    return value;
}
/**
 * Asserts a hash- or MAC-like instance has not been destroyed or finished.
 * @param instance - Stateful instance to validate.
 * @param checkFinished - Whether to reject finished instances.
 * When `false`, only `destroyed` is checked.
 * @throws If the hash instance has already been destroyed or finalized. {@link Error}
 * @example
 * Guards against calling `update()` or `digest()` on a finished hash.
 *
 * ```ts
 * aexists({ destroyed: false, finished: false });
 * ```
 */
function aexists(instance, checkFinished = true) {
    if (instance.destroyed)
        throw new Error('Hash instance has been destroyed');
    if (checkFinished && instance.finished)
        throw new Error('Hash#digest() has already been called');
}
/**
 * Asserts output is a properly-sized byte array.
 * @param out - Output buffer to validate.
 * @param instance - Hash-like instance providing `outputLen`.
 * This is the relaxed `digestInto()`-style contract: output must be at least `outputLen`,
 * unlike one-shot cipher helpers elsewhere in the repo that often require exact lengths.
 * @throws On wrong argument types. {@link TypeError}
 * @param onlyAligned - Whether `out` must be 4-byte aligned for zero-allocation word views.
 * @throws On wrong output buffer lengths. {@link RangeError}
 * @throws On wrong output buffer alignment. {@link Error}
 * @example
 * Verifies that a caller-provided output buffer is large enough.
 *
 * ```ts
 * aoutput(new Uint8Array(16), { outputLen: 16 });
 * ```
 */
function aoutput(out, instance, onlyAligned = false) {
    abytes(out, undefined, 'output');
    const min = instance.outputLen;
    if (out.length < min) {
        throw new RangeError('digestInto() expects output buffer of length at least ' + min);
    }
    if (onlyAligned && !isAligned32(out))
        throw new Error('invalid output, must be aligned');
}
/**
 * Casts a typed-array view to Uint8Array.
 * @param arr - Typed-array view to reinterpret.
 * @returns Uint8Array view over the same bytes.
 * @example
 * Views 32-bit words as raw bytes without copying.
 *
 * ```ts
 * u8(new Uint32Array([1]));
 * ```
 */
function u8(arr) {
    return new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
}
/**
 * Casts a typed-array view to Uint32Array.
 * @param arr - Typed-array view to reinterpret.
 * @returns Uint32Array view over the same bytes. Callers are expected to provide a
 * 4-byte-aligned offset; trailing `1..3` bytes are silently dropped.
 * @example
 * Views a byte buffer as 32-bit words for block processing.
 *
 * ```ts
 * u32(new Uint8Array(4));
 * ```
 */
function u32(arr) {
    return new Uint32Array(arr.buffer, arr.byteOffset, Math.floor(arr.byteLength / 4));
}
/**
 * Zeroizes typed arrays in place.
 * Warning: JS provides no guarantees.
 * @param arrays - Arrays to wipe.
 * @example
 * Wipes a temporary key buffer after use.
 *
 * ```ts
 * const bytes = new Uint8Array([1]);
 * clean(bytes);
 * ```
 */
function clean(...arrays) {
    for (let i = 0; i < arrays.length; i++) {
        arrays[i].fill(0);
    }
}
/**
 * Creates a DataView for byte-level manipulation.
 * @param arr - Typed-array view to wrap.
 * @returns DataView over the same bytes.
 * @example
 * Creates an endian-aware view for length encoding.
 *
 * ```ts
 * createView(new Uint8Array(4));
 * ```
 */
function createView(arr) {
    return new DataView(arr.buffer, arr.byteOffset, arr.byteLength);
}
/**
 * Whether the current platform is little-endian.
 * Most are; some IBM systems are not.
 */
const isLE = /* @__PURE__ */ (() => new Uint8Array(new Uint32Array([0x11223344]).buffer)[0] === 0x44)();
/**
 * Reverses byte order of one 32-bit word.
 * @param word - Unsigned 32-bit word to swap.
 * @returns The same word with bytes reversed.
 * @example
 * Swaps a big-endian word into little-endian byte order.
 *
 * ```ts
 * byteSwap(0x11223344);
 * ```
 */
const byteSwap = (word) => ((word << 24) & 0xff000000) |
    ((word << 8) & 0xff0000) |
    ((word >>> 8) & 0xff00) |
    ((word >>> 24) & 0xff);
/**
 * Normalizes one 32-bit word to the little-endian representation expected by cipher cores.
 * @param n - Unsigned 32-bit word to normalize.
 * @returns Little-endian normalized word on big-endian hosts, else the input word unchanged.
 * @example
 * Normalizes a host-endian word before passing it into an ARX/AES core.
 *
 * ```ts
 * swap8IfBE(0x11223344);
 * ```
 */
const swap8IfBE = isLE
    ? (n) => n
    : (n) => byteSwap(n) >>> 0;
/**
 * Byte-swaps every word of a Uint32Array in place.
 * @param arr - Uint32Array whose words should be swapped.
 * @returns The same array after in-place byte swapping.
 * @example
 * Swaps every 32-bit word in a word-view buffer.
 *
 * ```ts
 * byteSwap32(new Uint32Array([0x11223344]));
 * ```
 */
const byteSwap32 = (arr) => {
    for (let i = 0; i < arr.length; i++)
        arr[i] = byteSwap(arr[i]);
    return arr;
};
/**
 * Normalizes a Uint32Array view to the little-endian representation expected by cipher cores.
 * @param u - Word view to normalize in place.
 * @returns Little-endian normalized word view.
 * @example
 * Normalizes a word-view buffer before block processing.
 *
 * ```ts
 * swap32IfBE(new Uint32Array([0x11223344]));
 * ```
 */
const swap32IfBE = isLE
    ? (u) => u
    : byteSwap32;
/**
 * Checks if two U8A use same underlying buffer and overlaps.
 * This is invalid and can corrupt data.
 * @param a - First byte view.
 * @param b - Second byte view.
 * @returns `true` when the views overlap in memory.
 * @example
 * Detects whether two slices alias the same backing buffer.
 *
 * ```ts
 * overlapBytes(new Uint8Array(4), new Uint8Array(4));
 * ```
 */
function overlapBytes(a, b) {
    // Zero-length views cannot overwrite anything, even if their offset sits inside another range.
    if (!a.byteLength || !b.byteLength)
        return false;
    return (a.buffer === b.buffer && // best we can do, may fail with an obscure Proxy
        a.byteOffset < b.byteOffset + b.byteLength && // a starts before b end
        b.byteOffset < a.byteOffset + a.byteLength // b starts before a end
    );
}
/**
 * If input and output overlap and input starts before output, we will overwrite end of input before
 * we start processing it, so this is not supported for most ciphers
 * (except chacha/salsa, which were designed for this)
 * @param input - Input bytes.
 * @param output - Output bytes.
 * @throws If the output view would overwrite unread input bytes. {@link Error}
 * @example
 * Rejects an in-place layout that would overwrite unread input bytes.
 *
 * ```ts
 * complexOverlapBytes(new Uint8Array(4), new Uint8Array(4));
 * ```
 */
function complexOverlapBytes(input, output) {
    // This is very cursed. It works somehow, but I'm completely unsure,
    // reasoning about overlapping aligned windows is very hard.
    if (overlapBytes(input, output) && input.byteOffset < output.byteOffset)
        throw new Error('complex overlap of input and output is not supported');
}
/**
 * Merges user options into defaults.
 * @param defaults - Default option values.
 * @param opts - User-provided overrides.
 * @returns Combined options object.
 * The merge mutates `defaults` in place and returns the same object.
 * @throws If options are missing or not an object. {@link Error}
 * @example
 * Applies user overrides to the default cipher options.
 *
 * ```ts
 * checkOpts({ rounds: 20 }, { rounds: 8 });
 * ```
 */
function checkOpts(defaults, opts) {
    if (opts == null || typeof opts !== 'object')
        throw new Error('options must be defined');
    const merged = Object.assign(defaults, opts);
    return merged;
}
/**
 * Compares two byte arrays in kinda constant time once lengths already match.
 * @param a - First byte array.
 * @param b - Second byte array.
 * @returns `true` when the arrays contain the same bytes. Different lengths still return early.
 * @example
 * Compares an expected authentication tag with the received one.
 *
 * ```ts
 * equalBytes(new Uint8Array([1]), new Uint8Array([1]));
 * ```
 */
function equalBytes(a, b) {
    if (a.length !== b.length)
        return false;
    let diff = 0;
    for (let i = 0; i < a.length; i++)
        diff |= a[i] ^ b[i];
    return diff === 0;
}
/**
 * Wraps a keyed MAC constructor into a one-shot helper with `.create()`.
 * @param keyLen - Valid probe-key length used to read static metadata once.
 * The probe key is only used for `outputLen` / `blockLen`, so callers with several valid key sizes
 * can pass any representative size as long as those values stay fixed.
 * @param macCons - Keyed MAC constructor or factory.
 * @param fromMsg - Optional adapter that derives extra constructor args from the one-shot message.
 * @returns Callable MAC helper with `.create()`.
 */
function wrapMacConstructor(keyLen, macCons, fromMsg) {
    const mac = macCons;
    const getArgs = (fromMsg || (() => []));
    const macC = (msg, key) => mac(key, ...getArgs(msg))
        .update(msg)
        .digest();
    const tmp = mac(new Uint8Array(keyLen), ...getArgs(new Uint8Array(0)));
    macC.outputLen = tmp.outputLen;
    macC.blockLen = tmp.blockLen;
    macC.create = (key, ...args) => mac(key, ...args);
    return macC;
}
/**
 * Wraps a cipher: validates args, ensures encrypt() can only be called once.
 * Used internally by the exported cipher constructors.
 * Output-buffer support is inferred from the wrapped `encrypt` / `decrypt`
 * arity (`fn.length === 2`), and tag-bearing constructors are expected to use
 * `args[1]` for optional AAD.
 * @__NO_SIDE_EFFECTS__
 * @param params - Static cipher metadata. See {@link CipherParams}.
 * @param constructor - Cipher constructor.
 * @returns Wrapped constructor with validation.
 */
const wrapCipher = (params, constructor) => {
    function wrappedCipher(key, ...args) {
        // Validate key
        abytes(key, undefined, 'key');
        // Validate nonce if nonceLength is present
        if (params.nonceLength !== undefined) {
            const nonce = args[0];
            abytes(nonce, params.varSizeNonce ? undefined : params.nonceLength, 'nonce');
        }
        // Validate AAD if tagLength present
        const tagl = params.tagLength;
        if (tagl && args[1] !== undefined)
            abytes(args[1], undefined, 'AAD');
        const cipher = constructor(key, ...args);
        const checkOutput = (fnLength, output) => {
            if (output !== undefined) {
                if (fnLength !== 2)
                    throw new Error('cipher output not supported');
                abytes(output, undefined, 'output');
            }
        };
        // Create wrapped cipher with validation and single-use encryption
        let called = false;
        const wrCipher = {
            encrypt(data, output) {
                if (called)
                    throw new Error('cannot encrypt() twice with same key + nonce');
                called = true;
                abytes(data);
                checkOutput(cipher.encrypt.length, output);
                return cipher.encrypt(data, output);
            },
            decrypt(data, output) {
                abytes(data);
                if (tagl && data.length < tagl)
                    throw new Error('"ciphertext" expected length bigger than tagLength=' + tagl);
                checkOutput(cipher.decrypt.length, output);
                return cipher.decrypt(data, output);
            },
        };
        return wrCipher;
    }
    Object.assign(wrappedCipher, params);
    return wrappedCipher;
};
/**
 * By default, returns u8a of length.
 * When out is available, it checks it for validity and uses it.
 * @param expectedLength - Required output length.
 * @param out - Optional destination buffer.
 * @param onlyAligned - Whether `out` must be 4-byte aligned.
 * @returns Output buffer ready for writing.
 * @throws On wrong argument types. {@link TypeError}
 * @throws If the provided output buffer has the wrong size or alignment. {@link Error}
 * @example
 * Reuses a caller-provided output buffer when lengths match.
 *
 * ```ts
 * getOutput(16, new Uint8Array(16));
 * ```
 */
function getOutput(expectedLength, out, onlyAligned = true) {
    if (out === undefined)
        return new Uint8Array(expectedLength);
    // Keep Buffer/cross-realm Uint8Array support here instead of trusting a shape-compatible object.
    abytes(out, undefined, 'output');
    if (out.length !== expectedLength)
        throw new Error('"output" expected Uint8Array of length ' + expectedLength + ', got: ' + out.length);
    if (onlyAligned && !isAligned32(out))
        throw new Error('invalid output, must be aligned');
    return out;
}
/**
 * Encodes data and AAD bit lengths into a 16-byte buffer.
 * @param dataLength - Data length in bits.
 * @param aadLength - AAD length in bits.
 * The serialized block is still `aadLength || dataLength`, matching GCM/Poly1305
 * conventions even though the helper parameter order is `(dataLength, aadLength)`.
 * @param isLE - Whether to encode lengths as little-endian.
 * @returns 16-byte length block.
 * @throws On wrong argument types passed to the endian validator. {@link TypeError}
 * @throws On wrong argument ranges or values. {@link RangeError}
 * @example
 * Builds the length block appended by GCM and Poly1305.
 *
 * ```ts
 * u64Lengths(16, 8, true);
 * ```
 */
function u64Lengths(dataLength, aadLength, isLE) {
    // Reject coercible non-number lengths like '10' and true before BigInt(...) accepts them.
    anumber(dataLength);
    anumber(aadLength);
    abool(isLE);
    const num = new Uint8Array(16);
    const view = createView(num);
    view.setBigUint64(0, BigInt(aadLength), isLE);
    view.setBigUint64(8, BigInt(dataLength), isLE);
    return num;
}
/**
 * Checks whether a byte array is aligned to a 4-byte offset.
 * @param bytes - Byte array to inspect.
 * @returns `true` when the view is 4-byte aligned.
 * @example
 * Checks whether a buffer can be safely viewed as Uint32Array.
 *
 * ```ts
 * isAligned32(new Uint8Array(4));
 * ```
 */
function isAligned32(bytes) {
    return bytes.byteOffset % 4 === 0;
}
/**
 * Copies bytes into a new Uint8Array.
 * @param bytes - Bytes to copy.
 * @returns Copied byte array.
 * @throws On wrong argument types. {@link TypeError}
 * @example
 * Copies input into an aligned Uint8Array before block processing.
 *
 * ```ts
 * copyBytes(new Uint8Array([1, 2]));
 * ```
 */
function copyBytes(bytes) {
    // `Uint8Array.from(...)` would also accept arrays / other typed arrays. Keep this helper strict
    // because callers use it at byte-validation boundaries before mutating the detached copy.
    return Uint8Array.from(abytes(bytes));
}

/**
 * {@link https://en.wikipedia.org/wiki/Advanced_Encryption_Standard | AES}
 * a.k.a. Advanced Encryption Standard
 * is a variant of Rijndael block cipher, standardized by NIST in 2001.
 * We provide the fastest available pure JS implementation.
 *
 * `cipher = encrypt(block, key)`
 *
 * Data is split into 128-bit blocks.
 * Encrypted in 10/12/14 rounds (128/192/256 bits). In every round:
 * 1. **S-box**, table substitution
 * 2. **Shift rows**, cyclic shift left of all rows of data array
 * 3. **Mix columns**, multiplying every column by fixed polynomial
 * 4. **Add round key**, round_key xor i-th column of array
 *
 * Check out
 * {@link https://csrc.nist.gov/files/pubs/fips/197/final/docs/fips-197.pdf | FIPS-197},
 * {@link https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-38G.pdf | NIST 800-38G},
 * and {@link https://csrc.nist.gov/csrc/media/projects/cryptographic-standards-and-guidelines/documents/aes-development/rijndael-ammended.pdf | original proposal}.
 * @module
 */
const BLOCK_SIZE$2 = 16;
// AES operates on 16-byte blocks, i.e. 4 32-bit words.
const BLOCK_SIZE32 = 4;
// Shared zero block (`0^128`) used by GCM's `H = CIPH_K(0^128)` / J0 scratch
// and by CMAC / SIV helpers; callers take `.slice()` before mutating it.
const EMPTY_BLOCK = /* @__PURE__ */ new Uint8Array(BLOCK_SIZE$2);
// RFC 5297 §2.1 / §2.4: S2V uses `<one> = 0^127 || 1` for the `n = 0` special case.
const ONE_BLOCK = /* @__PURE__ */ Uint8Array.from([
    0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x01,
]);
const POLY = 0x11b; // 1 + x + x**3 + x**4 + x**8
// Validates plain AES key sizes only; AES-SIV's doubled-key contract is checked elsewhere.
function validateKeyLength(key) {
    if (![16, 24, 32].includes(key.length))
        throw new Error('"aes key" expected Uint8Array of length 16/24/32, got length=' + key.length);
}
// TODO: remove multiplication, binary ops only
// Doubles one GF(2^8) field element; callers are expected to stay in byte range.
// FIPS 197 upd1 §4.3 equation (4.5): XTIMES(b) left-shifts by one and, when
// b7=1, reduces by m(x); using POLY=0x11b here yields the same byte result
// as XORing with {1b} after the shift.
function mul2(n) {
    return (n << 1) ^ (POLY & -(n >> 7));
}
// Shift-and-add multiplication in GF(2^8); callers are expected to pass byte values.
// FIPS 197 upd1 §4.3 equation (4.7): general products are XORs of repeated
// XTIMES() multiples, e.g. {57}•{13} = {57}⊕{ae}⊕{07}.
function mul(a, b) {
    let res = 0;
    for (; b > 0; b >>= 1) {
        // Usual shift-and-add step in GF(2^8), not a scalar-multiplication ladder.
        res ^= a & -(b & 1); // if (b&1) res ^=a (but const-time).
        a = mul2(a); // a = 2*a
    }
    return res;
}
/**
 * Increments a counter block with wrap around.
 * AES call sites here currently use the big-endian branch, but the helper supports both layouts.
 * NIST SP 800-38A Appendix B.1 and SP 800-38D §6.2 increment the
 * least-significant/rightmost bits.
 * `isLE=false` matches that standard counter-block layout, while `isLE=true`
 * is a generic extension for non-AES callers.
 * The implementation keeps a 32-bit bitwise carry path, so `carry` is capped at `0xffffff00`;
 * larger values throw instead of silently overflowing before the next-byte propagation step.
 */
// Keep the helper explicitly typed so `--isolatedDeclarations` can expose it
// through the test-only `__TESTS` export without inference errors.
const incBytes = (data, isLE, carry = 1) => {
    // Keep `carry + byte <= 0xffffffff` so the `| 0` / `>>> 8` path below
    // never truncates a real carry bit.
    if (!Number.isSafeInteger(carry) || carry > 0xffffff00)
        throw new Error('incBytes: wrong carry ' + carry);
    abytes(data);
    for (let i = 0; i < data.length; i++) {
        const pos = !isLE ? data.length - 1 - i : i;
        carry = (carry + (data[pos] & 0xff)) | 0;
        data[pos] = carry & 0xff;
        carry >>>= 8;
    }
};
// AES S-box is generated using finite field inversion,
// an affine transform, and xor of a constant 0x63.
const sbox = /* @__PURE__ */ (() => {
    const t = new Uint8Array(256);
    // Repeated multiplication by {03} walks all 255 nonzero field elements
    // once, so t[255 - i] is the multiplicative inverse of t[i] for the
    // affine step.
    for (let i = 0, x = 1; i < 256; i++, x ^= mul2(x))
        t[i] = x;
    const box = new Uint8Array(256);
    // FIPS 197 upd1 §5.1.1: SBOX({00}) = {63} because the inverse step leaves
    // {00} at {00}, then the affine transform xors in c = {63}.
    box[0] = 0x63;
    for (let i = 0; i < 255; i++) {
        let x = t[255 - i];
        x |= x << 8;
        box[t[i]] = (x ^ (x >> 4) ^ (x >> 5) ^ (x >> 6) ^ (x >> 7) ^ 0x63) & 0xff;
    }
    clean(t);
    return box;
})();
// FIPS 197 upd1 §5.3.2: INVSBOX() is derived from SBOX() by swapping input
// and output roles (Table 6).
// `indexOf` is only used once at module init, so the quadratic setup cost stays off hot paths.
const invSbox = /* @__PURE__ */ sbox.map((_, j) => sbox.indexOf(j));
// FIPS 197 upd1 §5.2: ROTWORD([a0,a1,a2,a3]) = [a1,a2,a3,a0]; with this LE
// word packing that is a right rotate by 8 bits.
const rotr32_8 = (n) => (n << 24) | (n >>> 8);
// LE T-table helper: rotates one precomputed word by one byte so T1/T2/T3
// reuse T0's substitution/mix result in the other byte lanes.
const rotl32_8 = (n) => (n << 8) | (n >>> 24);
// T-table is optimization suggested in 5.2 of original proposal (missed from FIPS-197). Changes:
// - LE instead of BE
// - bigger tables: T0 and T1 are merged into T01 table and T2 & T3 into T23;
//   so index is u16, instead of u8. This speeds up things, unexpectedly
function genTtable(sbox, fn) {
    if (sbox.length !== 256)
        throw new Error('Wrong sbox length');
    const T0 = new Uint32Array(256).map((_, j) => fn(sbox[j]));
    const T1 = T0.map(rotl32_8);
    const T2 = T1.map(rotl32_8);
    const T3 = T2.map(rotl32_8);
    // Pre-xor adjacent lanes so apply0123/applySbox can fetch two substituted
    // byte lanes per lookup in the LE round layout.
    const T01 = new Uint32Array(256 * 256);
    const T23 = new Uint32Array(256 * 256);
    const sbox2 = new Uint16Array(256 * 256);
    for (let i = 0; i < 256; i++) {
        for (let j = 0; j < 256; j++) {
            const idx = i * 256 + j;
            T01[idx] = T0[i] ^ T1[j];
            T23[idx] = T2[i] ^ T3[j];
            sbox2[idx] = (sbox[i] << 8) | sbox[j];
        }
    }
    return { sbox, sbox2, T0, T1, T2, T3, T01, T23 };
}
// Forward round precompute: the packed word stores the MIXCOLUMNS row
// [{02},{01},{01},{03}] in LE byte-lane order, and the returned `sbox2`
// is also reused by key expansion and the final round.
const tableEncoding = /* @__PURE__ */ genTtable(sbox, (s) => (mul(s, 3) << 24) | (s << 16) | (s << 8) | mul(s, 2));
// Inverse round precompute: the packed word stores the INVMIXCOLUMNS row
// [{0e},{09},{0d},{0b}] in LE byte-lane order, and the tables are reused
// by decrypt() and expandKeyDecLE().
const tableDecoding = /* @__PURE__ */ genTtable(invSbox, (s) => (mul(s, 11) << 24) | (mul(s, 13) << 16) | (mul(s, 9) << 8) | mul(s, 14));
// FIPS 197 upd1 §5.2 Table 5: left-most bytes of Rcon[j] = x^(j-1), generated by repeated XTIMES().
const xPowers = /* @__PURE__ */ (() => {
    const p = new Uint8Array(16);
    for (let i = 0, x = 1; i < 16; i++, x = mul2(x))
        p[i] = x;
    return p;
})();
/** Forward AES key expansion used across ECB/CBC/CTR/GCM/CMAC/KW-style paths. */
function expandKeyLE(key) {
    abytes(key);
    const len = key.length;
    validateKeyLength(key);
    const { sbox2 } = tableEncoding;
    const toClean = [];
    // Copy on BE or misaligned inputs so the LE word normalization below never
    // mutates caller key bytes in place.
    if (!isLE || !isAligned32(key))
        toClean.push((key = copyBytes(key)));
    const k32 = swap32IfBE(u32(key));
    const Nk = k32.length;
    // `applySbox` normally reads one byte lane from each argument; repeating
    // `n` across all four lanes turns it into SUBWORD(n).
    const subByte = (n) => applySbox(sbox2, n, n, n, n);
    // AES key sizes are 16/24/32 bytes, so len + 28 yields the 44/52/60
    // schedule words from FIPS 197 §5.2 / Table 3.
    const xk = new Uint32Array(len + 28); // expanded key
    xk.set(k32);
    // 4.3.1 Key expansion
    for (let i = Nk; i < xk.length; i++) {
        let t = xk[i - 1];
        if (i % Nk === 0)
            t = subByte(rotr32_8(t)) ^ xPowers[i / Nk - 1];
        else if (Nk > 6 && i % Nk === 4)
            t = subByte(t);
        xk[i] = xk[i - Nk] ^ t;
    }
    clean(...toClean);
    return xk;
}
function expandKeyDecLE(key) {
    const encKey = expandKeyLE(key);
    const xk = encKey.slice();
    const Nk = encKey.length;
    const { sbox2 } = tableEncoding;
    const { T0, T1, T2, T3 } = tableDecoding;
    // Local decrypt() walks round keys forward from xk[0], so reverse the
    // encryption round-key blocks first before applying the equivalent-inverse
    // middle-round transform.
    for (let i = 0; i < Nk; i += 4) {
        for (let j = 0; j < 4; j++)
            xk[i + j] = encKey[Nk - i - 4 + j];
    }
    clean(encKey);
    // Apply InvMixColumn to the reversed round keys using the same LE sbox2
    // packing as the forward path.
    // apply InvMixColumn except first & last round
    for (let i = 4; i < Nk - 4; i++) {
        const x = xk[i];
        const w = applySbox(sbox2, x, x, x, x);
        xk[i] = T0[w & 0xff] ^ T1[(w >>> 8) & 0xff] ^ T2[(w >>> 16) & 0xff] ^ T3[w >>> 24];
    }
    return xk;
}
// Apply tables
function apply0123(T01, T23, s0, s1, s2, s3) {
    // `T01` takes the low byte lane from `s0` plus the next lane from `s1`;
    // `T23` does the same for `s2`/`s3`.
    // Equivalent to `T0[s0&0xff] ^ T1[(s1>>>8)&0xff] ^ T2[(s2>>>16)&0xff] ^
    // T3[s3>>>24]`, but with two merged-table fetches.
    return (T01[((s0 << 8) & 0xff00) | ((s1 >>> 8) & 0xff)] ^
        T23[((s2 >>> 8) & 0xff00) | ((s3 >>> 24) & 0xff)]);
}
function applySbox(sbox2, s0, s1, s2, s3) {
    // `sbox2` packs two substituted byte lanes at a time in the same LE
    // layout used by the round code.
    // Equivalent to `SBOX(byte0(s0)) | SBOX(byte1(s1))<<8 |
    // SBOX(byte2(s2))<<16 | SBOX(byte3(s3))<<24`.
    return (sbox2[(s0 & 0xff) | (s1 & 0xff00)] |
        (sbox2[((s2 >>> 16) & 0xff) | ((s3 >>> 16) & 0xff00)] << 16));
}
function encrypt$1(xk, s0, s1, s2, s3) {
    const { sbox2, T01, T23 } = tableEncoding;
    let k = 0;
    ((s0 ^= xk[k++]), (s1 ^= xk[k++]), (s2 ^= xk[k++]), (s3 ^= xk[k++]));
    // `xk` has Nr+1 round-key blocks, so after the initial AddRoundKey and the
    // final S-box-only round there are Nr-1 full table/MixColumns rounds left.
    const rounds = xk.length / 4 - 2;
    for (let i = 0; i < rounds; i++) {
        const t0 = xk[k++] ^ apply0123(T01, T23, s0, s1, s2, s3);
        const t1 = xk[k++] ^ apply0123(T01, T23, s1, s2, s3, s0);
        const t2 = xk[k++] ^ apply0123(T01, T23, s2, s3, s0, s1);
        const t3 = xk[k++] ^ apply0123(T01, T23, s3, s0, s1, s2);
        ((s0 = t0), (s1 = t1), (s2 = t2), (s3 = t3));
    }
    // last round (without mixcolumns, so using SBOX2 table)
    const t0 = xk[k++] ^ applySbox(sbox2, s0, s1, s2, s3);
    const t1 = xk[k++] ^ applySbox(sbox2, s1, s2, s3, s0);
    const t2 = xk[k++] ^ applySbox(sbox2, s2, s3, s0, s1);
    const t3 = xk[k++] ^ applySbox(sbox2, s3, s0, s1, s2);
    return { s0: t0, s1: t1, s2: t2, s3: t3 };
}
// Can't be merged with encrypt: arg positions for apply0123 / applySbox are different
function decrypt$1(xk, s0, s1, s2, s3) {
    const { sbox2, T01, T23 } = tableDecoding;
    let k = 0;
    ((s0 ^= xk[k++]), (s1 ^= xk[k++]), (s2 ^= xk[k++]), (s3 ^= xk[k++]));
    // With `expandKeyDecLE()` the round keys are already reversed and middle
    // rounds are InvMixColumns-adjusted, so this loop follows the equivalent
    // inverse cipher order directly.
    const rounds = xk.length / 4 - 2;
    for (let i = 0; i < rounds; i++) {
        const t0 = xk[k++] ^ apply0123(T01, T23, s0, s3, s2, s1);
        const t1 = xk[k++] ^ apply0123(T01, T23, s1, s0, s3, s2);
        const t2 = xk[k++] ^ apply0123(T01, T23, s2, s1, s0, s3);
        const t3 = xk[k++] ^ apply0123(T01, T23, s3, s2, s1, s0);
        ((s0 = t0), (s1 = t1), (s2 = t2), (s3 = t3));
    }
    // Final equivalent-inverse round omits InvMixColumns, so use inverse
    // S-box lanes in InvShiftRows order.
    const t0 = xk[k++] ^ applySbox(sbox2, s0, s3, s2, s1);
    const t1 = xk[k++] ^ applySbox(sbox2, s1, s0, s3, s2);
    const t2 = xk[k++] ^ applySbox(sbox2, s2, s1, s0, s3);
    const t3 = xk[k++] ^ applySbox(sbox2, s3, s2, s1, s0);
    return { s0: t0, s1: t1, s2: t2, s3: t3 };
}
function ctrCounter(xk, nonce, src, dst) {
    abytes(nonce, BLOCK_SIZE$2, 'nonce');
    abytes(src);
    const srcLen = src.length;
    dst = getOutput(srcLen, dst);
    complexOverlapBytes(src, dst);
    // Internal helper: mutate `nonce` in place as the live counter block so
    // each encrypted block uses the next CTR value.
    const ctr = nonce;
    const c32 = u32(ctr);
    const src32 = u32(src);
    const dst32 = u32(dst);
    // Fill block (empty, ctr=0)
    let { s0, s1, s2, s3 } = encrypt$1(xk, swap8IfBE(c32[0]), swap8IfBE(c32[1]), swap8IfBE(c32[2]), swap8IfBE(c32[3]));
    // process blocks
    for (let i = 0; i + 4 <= src32.length; i += 4) {
        dst32[i + 0] = src32[i + 0] ^ swap8IfBE(s0);
        dst32[i + 1] = src32[i + 1] ^ swap8IfBE(s1);
        dst32[i + 2] = src32[i + 2] ^ swap8IfBE(s2);
        dst32[i + 3] = src32[i + 3] ^ swap8IfBE(s3);
        incBytes(ctr, false, 1); // Full 128 bit counter with wrap around
        ({ s0, s1, s2, s3 } = encrypt$1(xk, swap8IfBE(c32[0]), swap8IfBE(c32[1]), swap8IfBE(c32[2]), swap8IfBE(c32[3])));
    }
    // NIST SP 800-38A CTR mode uses the leading `u` bits of the next output
    // block for the final short block.
    // It's possible to handle > u32 fast, but is it worth it?
    const start = BLOCK_SIZE$2 * Math.floor(src32.length / BLOCK_SIZE32);
    if (start < srcLen) {
        const b32 = new Uint32Array([s0, s1, s2, s3]);
        swap32IfBE(b32);
        const buf = u8(b32);
        for (let i = start, pos = 0; i < srcLen; i++, pos++)
            dst[i] = src[i] ^ buf[pos];
        clean(b32);
    }
    // Unsafe mutable-counter API only advances whole blocks. Callers that want to
    // resume after consuming part of this block must re-run from the same counter
    // with left-padding and strip the already-consumed prefix themselves.
    return dst;
}
// AES CTR with overflowing 32 bit counter
// It's possible to do 32le significantly simpler (and probably faster) by using u32.
// But, we need both, and perf bottleneck is in ghash anyway.
// Unsafe 32-bit CTR helper: mutates `nonce` in place, expects aligned `src`/`dst`,
// and uses `isLE` to choose which 32-bit counter word is incremented.
function ctr32(xk, isLE, nonce, src, dst) {
    abytes(nonce, BLOCK_SIZE$2, 'nonce');
    abytes(src);
    dst = getOutput(src.length, dst);
    const ctr = nonce; // write new value to nonce, so it can be re-used
    const c32 = u32(ctr);
    const view = createView(ctr);
    const src32 = u32(src);
    const dst32 = u32(dst);
    // NIST SP 800-38D GCTR increments the rightmost 32 bits of J0, while
    // RFC 8452 AES-GCM-SIV increments the first 32 bits as a little-endian u32.
    const ctrPos = isLE ? 0 : 12;
    const srcLen = src.length;
    // Fill block (empty, ctr=0)
    let ctrNum = view.getUint32(ctrPos, isLE); // read current counter value
    let { s0, s1, s2, s3 } = encrypt$1(xk, swap8IfBE(c32[0]), swap8IfBE(c32[1]), swap8IfBE(c32[2]), swap8IfBE(c32[3]));
    // process blocks
    for (let i = 0; i + 4 <= src32.length; i += 4) {
        dst32[i + 0] = src32[i + 0] ^ swap8IfBE(s0);
        dst32[i + 1] = src32[i + 1] ^ swap8IfBE(s1);
        dst32[i + 2] = src32[i + 2] ^ swap8IfBE(s2);
        dst32[i + 3] = src32[i + 3] ^ swap8IfBE(s3);
        ctrNum = (ctrNum + 1) >>> 0; // u32 wrap
        view.setUint32(ctrPos, ctrNum, isLE);
        ({ s0, s1, s2, s3 } = encrypt$1(xk, swap8IfBE(c32[0]), swap8IfBE(c32[1]), swap8IfBE(c32[2]), swap8IfBE(c32[3])));
    }
    // leftovers (less than a block)
    const start = BLOCK_SIZE$2 * Math.floor(src32.length / BLOCK_SIZE32);
    if (start < srcLen) {
        const b32 = new Uint32Array([s0, s1, s2, s3]);
        swap32IfBE(b32);
        const buf = u8(b32);
        for (let i = start, pos = 0; i < srcLen; i++, pos++)
            dst[i] = src[i] ^ buf[pos];
        clean(b32);
    }
    // Same unsafe contract as ctrCounter(): only full blocks advance the stored
    // mutable counter state; partial-block continuation is caller-managed.
    return dst;
}
function isBytes32(a) {
    // Plain `instanceof Uint32Array` is too strict for cross-realm expanded-key views.
    // This is only a best-effort unsafe-export guard, not a provenance proof for `expandKeyLE`.
    return (a instanceof Uint32Array || (ArrayBuffer.isView(a) && a.constructor.name === 'Uint32Array'));
}
// Unsafe single-block helpers: mutate `block` in place and require its 16-byte
// Uint8Array view to be 4-byte aligned because `u32(block)` reinterprets it.
function encryptBlock$1(xk, block) {
    abytes(block, 16, 'block');
    if (!isBytes32(xk))
        throw new Error('_encryptBlock accepts result of expandKeyLE');
    const b32 = u32(block);
    swap32IfBE(b32);
    let { s0, s1, s2, s3 } = encrypt$1(xk, b32[0], b32[1], b32[2], b32[3]);
    ((b32[0] = s0), (b32[1] = s1), (b32[2] = s2), (b32[3] = s3));
    swap32IfBE(b32);
    return block;
}
function decryptBlock(xk, block) {
    abytes(block, 16, 'block');
    if (!isBytes32(xk))
        throw new Error('_decryptBlock accepts result of expandKeyLE');
    const b32 = u32(block);
    swap32IfBE(b32);
    let { s0, s1, s2, s3 } = decrypt$1(xk, b32[0], b32[1], b32[2], b32[3]);
    ((b32[0] = s0), (b32[1] = s1), (b32[2] = s2), (b32[3] = s3));
    swap32IfBE(b32);
    return block;
}
//#region CMAC
/**
 * Left-shift by one bit and conditionally XOR with 0x87:
 * ```
 * if MSB(L) is equal to 0
 * then    K1 := L << 1;
 * else    K1 := (L << 1) XOR const_Rb;
 * ```
 *
 * Specs:
 * {@link https://www.rfc-editor.org/rfc/rfc4493.html#section-2.3 | RFC 4493 Section 2.3},
 * {@link https://datatracker.ietf.org/doc/html/rfc5297.html#section-2.3 | RFC 5297 Section 2.3}
 *
 * @returns modified `block` (for chaining)
 */
function dbl(block) {
    let carry = 0;
    // Left shift by 1 bit
    for (let i = BLOCK_SIZE$2 - 1; i >= 0; i--) {
        const newCarry = (block[i] & 0x80) >>> 7;
        block[i] = (block[i] << 1) | carry;
        carry = newCarry;
    }
    // XOR with 0x87 if there was a carry from the most significant bit
    if (carry) {
        // RFC 4493 §2.3 / RFC 5297 §2.1: 0x87 is const_Rb for doubling in the
        // CMAC/S2V finite field with primitive polynomial x^128 + x^7 + x^2 + x + 1.
        block[BLOCK_SIZE$2 - 1] ^= 0x87;
    }
    return block;
}
/**
 * `a XOR b`, running in-place on `a`.
 * @param a left operand and output
 * @param b right operand
 * @returns `a` (for chaining)
 */
function xorBlock(a, b) {
    if (a.length !== b.length)
        throw new Error('xorBlock: blocks must have same length');
    for (let i = 0; i < a.length; i++) {
        a[i] = a[i] ^ b[i];
    }
    return a;
}
/**
 * xorend as defined in
 * {@link https://datatracker.ietf.org/doc/html/rfc5297.html#section-2.1 | RFC 5297 Section 2.1}.
 *
 * ```
 * leftmost(A, len(A)-len(B)) || (rightmost(A, len(B)) xor B)
 * ```
 *
 * Mutates `a` in place so the left prefix stays untouched and only the
 * rightmost `len(B)` bytes are xored with `b`.
 */
function xorend(a, b) {
    if (b.length > a.length) {
        throw new Error('xorend: len(B) must be less than or equal to len(A)');
    }
    // keep leftmost part of `a` unchanged
    // and xor only the rightmost part:
    const offset = a.length - b.length;
    for (let i = 0; i < b.length; i++) {
        a[offset + i] = a[offset + i] ^ b[i];
    }
    return a;
}
/**
 * Internal CMAC class.
 */
class _CMAC {
    blockLen = BLOCK_SIZE$2;
    outputLen = BLOCK_SIZE$2;
    // CMAC can only decide between `K1` and `K2` once the true final block is known,
    // so updates process older blocks eagerly but keep one pending block buffered.
    buffer;
    pos;
    finished;
    destroyed;
    k1;
    k2;
    x;
    xk;
    constructor(key) {
        abytes(key);
        validateKeyLength(key);
        this.xk = expandKeyLE(key);
        this.buffer = new Uint8Array(BLOCK_SIZE$2);
        this.pos = 0;
        this.finished = false;
        this.destroyed = false;
        this.x = new Uint8Array(BLOCK_SIZE$2);
        // L = AES_encrypt(K, const_Zero)
        const L = new Uint8Array(BLOCK_SIZE$2);
        encryptBlock$1(this.xk, L);
        // Generate subkeys K1 and K2 from the main key according to
        // {@link https://www.rfc-editor.org/rfc/rfc4493.html#section-2.3 | RFC 4493 Section 2.3}
        // K1
        this.k1 = dbl(L);
        this.k2 = dbl(new Uint8Array(this.k1));
    }
    process(data) {
        // RFC 4493 §2.4 step 6 loop body: Y := X XOR M_i; X := AES-128(K, Y).
        xorBlock(this.x, data);
        encryptBlock$1(this.xk, this.x);
    }
    update(data) {
        if (this.destroyed)
            throw new Error('Hash instance has been destroyed');
        if (this.finished)
            throw new Error('Hash#digest() has already been called');
        abytes(data);
        let pos = 0;
        if (this.pos) {
            const take = Math.min(BLOCK_SIZE$2 - this.pos, data.length);
            this.buffer.set(data.subarray(0, take), this.pos);
            this.pos += take;
            pos = take;
            if (this.pos === BLOCK_SIZE$2 && pos < data.length) {
                this.process(this.buffer);
                this.pos = 0;
            }
        }
        // Keep one complete block buffered: an exact 16-byte tail may still be
        // M_n, and digestInto() must decide there whether RFC 4493 uses K1 or K2.
        while (pos + BLOCK_SIZE$2 < data.length) {
            this.process(data.subarray(pos, pos + BLOCK_SIZE$2));
            pos += BLOCK_SIZE$2;
        }
        if (pos < data.length) {
            this.buffer.set(data.subarray(pos), 0);
            this.pos = data.length - pos;
        }
        return this;
    }
    // See {@link https://www.rfc-editor.org/rfc/rfc4493.html#section-2.4 | RFC 4493 Section 2.4}.
    digestInto(out) {
        if (this.destroyed)
            throw new Error('Hash instance has been destroyed');
        if (this.finished)
            throw new Error('Hash#digest() has already been called');
        // `digestInto(out)` is the no-allocation fast path, so AES block re-use below
        // requires a 32-bit-aligned caller buffer instead of hidden temp copies.
        aoutput(out, this, true);
        this.finished = true;
        // `digestInto()` accepts out.length >= outputLen, so only the first block stores the tag.
        const view = out.subarray(0, this.outputLen);
        let last = new Uint8Array(BLOCK_SIZE$2);
        if (this.pos === BLOCK_SIZE$2) {
            // M_last := M_n XOR K1;
            last.set(this.buffer);
            xorBlock(last, this.k1);
        }
        else {
            // M_last := padding(M_n) XOR K2;
            //
            // [...] padding(x) is the concatenation of x and a single '1',
            // followed by the minimum number of '0's, so that the total length is
            // equal to 128 bits.
            last.set(this.buffer.subarray(0, this.pos));
            last[this.pos] = 0x80; // single '1' bit
            xorBlock(last, this.k2);
        }
        view.set(this.x); // X := AES_CBC(K, M_1..M_{n-1})
        xorBlock(view, last); // Y := X XOR M_last
        encryptBlock$1(this.xk, view); // T := AES-128(K, Y)
        clean(last);
    }
    digest() {
        const { buffer, outputLen } = this;
        this.digestInto(buffer);
        // Copy out before destroy() wipes the internal digest buffer in place.
        const res = buffer.slice(0, outputLen);
        this.destroy();
        return res;
    }
    destroy() {
        const { buffer, destroyed, x, xk, k1, k2 } = this;
        if (destroyed)
            return;
        this.destroyed = true;
        // Wipe the buffered tail, chaining value, expanded AES key, and both CMAC subkeys.
        clean(buffer, x, xk, k1, k2);
    }
}
/**
 * AES-CMAC (Cipher-based Message Authentication Code).
 * Specs: {@link https://www.rfc-editor.org/rfc/rfc4493.html | RFC 4493}.
 * @param msg - Message bytes to authenticate.
 * @param key - AES key bytes.
 * @returns 16-byte authentication tag. `cmac.create(...)` follows the same incremental MAC shape as
 * the other keyed helpers in this repo, including `blockLen`,
 * `outputLen`, `digestInto()` and `destroy()`.
 * @example
 * Authenticates a message with AES-CMAC and a fresh key.
 *
 * ```ts
 * import { cmac } from '@noble/ciphers/aes.js';
 * import { randomBytes } from '@noble/ciphers/utils.js';
 * const key = randomBytes(16);
 * cmac(new Uint8Array(), key);
 * ```
 */
// The 16-byte probe key is only used to read static metadata; runtime CMAC
// still accepts AES-128/192/256 keys.
const cmac = /* @__PURE__ */ wrapMacConstructor(16, (key) => new _CMAC(key));
/**
 * S2V (Synthetic Initialization Vector) function as described in
 * {@link https://datatracker.ietf.org/doc/html/rfc5297.html#section-2.4 | RFC 5297 Section 2.4}.
 *
 * ```
 * S2V(K, S1, ..., Sn) {
 *   if n = 0 then
 *     return V = AES-CMAC(K, <one>)
 *   fi
 *   D = AES-CMAC(K, <zero>)
 *   for i = 1 to n-1 do
 *     D = dbl(D) xor AES-CMAC(K, Si)
 *   done
 *   if len(Sn) >= 128 then
 *     T = Sn xorend D
 *   else
 *     T = dbl(D) xor pad(Sn)
 *   fi
 *   return V = AES-CMAC(K, T)
 * }
 * ```
 *
 * S2V takes a key and a vector of strings S1, S2, ..., Sn and returns a 128-bit string.
 * The S2V function is used to generate a synthetic IV for AES-SIV.
 *
 * @param key - AES key (128, 192, or 256 bits)
 * @param strings - Array of byte arrays to process
 * @returns 128-bit synthetic IV
 */
function s2v(key, strings) {
    validateKeyLength(key);
    const len = strings.length;
    if (len > 127) {
        // RFC 5297 §7 only proves S2V secure for at most 127 components; SIV
        // spends one of those on the plaintext, leaving at most 126 AAD inputs.
        throw new Error('s2v: number of input strings must be less than or equal to 127');
    }
    if (len === 0)
        return cmac(ONE_BLOCK, key);
    // D = AES-CMAC(K, <zero>)
    let d = cmac(EMPTY_BLOCK, key);
    // for i = 1 to n-1 do
    //   D = dbl(D) xor AES-CMAC(K, Si)
    for (let i = 0; i < len - 1; i++) {
        dbl(d);
        const cmacResult = cmac(strings[i], key);
        xorBlock(d, cmacResult);
        clean(cmacResult);
    }
    const s_n = strings[len - 1];
    // Earlier components are validated through cmac(...); validate the final one explicitly because
    // the Uint8Array.from()/set() paths below would otherwise coerce array-like inputs silently.
    abytes(s_n);
    let t;
    // if len(Sn) >= 128 then
    if (s_n.byteLength >= BLOCK_SIZE$2) {
        // T = Sn xorend D
        t = xorend(Uint8Array.from(s_n), d);
    }
    else {
        // pad(Sn):
        const paddedSn = new Uint8Array(BLOCK_SIZE$2);
        paddedSn.set(s_n);
        paddedSn[s_n.length] = 0x80; // padding: 0x80 followed by zeros
        // T = dbl(D) xor pad(Sn)
        t = xorBlock(dbl(d), paddedSn);
        clean(paddedSn);
    }
    // V = AES-CMAC(K, T)
    const result = cmac(t, key);
    clean(d, t);
    return result;
}
//#endregion
/**
 * Unsafe low-level internal methods. May change at any time.
 * Callers are expected to use reviewed expanded-key outputs, pass mutable and
 * aligned 16-byte blocks where required, and treat several helpers as in-place
 * mutations of their input buffers or counters.
 */
const unsafe = /* @__PURE__ */ Object.freeze({
    expandKeyLE,
    expandKeyDecLE,
    encrypt: encrypt$1,
    decrypt: decrypt$1,
    encryptBlock: encryptBlock$1,
    decryptBlock,
    ctrCounter,
    ctr32,
    dbl,
    xorBlock,
    xorend,
    s2v,
});

const BLOCK_SIZE$1 = 16;
const KEY_SIZES$1 = [16, 24, 32];
const NONCE_SIZES = [7, 8, 9, 10, 11, 12, 13];
const TAG_SIZES$1 = [4, 6, 8, 10, 12, 14, 16];
const DEFAULT_TAG_LENGTH$1 = 16;
const MIN_NONCE_LENGTH = 7;
const MAX_NONCE_LENGTH = 13;
const AAD_SHORT_LIMIT = 0xff00;
function createAesCcmComponent() {
    return {
        kind: 'aead',
        name: 'AES-CCM',
        keySizes: KEY_SIZES$1,
        nonceSizes: NONCE_SIZES,
        recommendedNonceSize: 12,
        tagSizes: TAG_SIZES$1,
        create(params, context) {
            return createAesCcmTransform(params, context);
        },
    };
}
/** Exported for focused unit tests; not part of the public package surface. */
function encodeCcmAad(aad) {
    return concatBytes(encodeCcmAadLength(aad.length), aad);
}
/**
 * Encode only the CCM AAD length prefix for a given length.
 * Accepts synthetic lengths so the `0xffff` form (>= 2^32) can be unit-tested
 * without allocating multi-gigabyte AAD buffers.
 */
function encodeCcmAadLength(aadLength) {
    if (aadLength === 0) {
        return new Uint8Array(0);
    }
    if (aadLength < AAD_SHORT_LIMIT) {
        const encoded = new Uint8Array(2);
        encoded[0] = (aadLength >>> 8) & 0xff;
        encoded[1] = aadLength & 0xff;
        return encoded;
    }
    if (aadLength < 0x100000000) {
        const encoded = new Uint8Array(6);
        encoded[0] = 0xff;
        encoded[1] = 0xfe;
        encoded[2] = (aadLength >>> 24) & 0xff;
        encoded[3] = (aadLength >>> 16) & 0xff;
        encoded[4] = (aadLength >>> 8) & 0xff;
        encoded[5] = aadLength & 0xff;
        return encoded;
    }
    const encoded = new Uint8Array(10);
    encoded[0] = 0xff;
    encoded[1] = 0xff;
    let remaining = aadLength;
    for (let i = 9; i >= 2; i--) {
        encoded[i] = remaining % 256;
        remaining = Math.floor(remaining / 256);
    }
    return encoded;
}
/**
 * Encode an unsigned integer into `byteLength` bytes at `out[offset]`.
 * Used for CCM length and counter fields.
 */
function encodeCcmBinaryLength(value, byteLength, out, offset) {
    let remaining = value;
    for (let i = byteLength - 1; i >= 0; i--) {
        out[offset + i] = remaining % 256;
        remaining = Math.floor(remaining / 256);
    }
    if (remaining !== 0) {
        throw new RangeError('AES-CCM plaintext is too large for the nonce length.');
    }
}
function createAesCcmTransform({ key }, context) {
    assertBytes(key, 'AES-CCM key');
    if (key.length !== 16 && key.length !== 24 && key.length !== 32) {
        throw new Error('AES key must be 128, 192, or 256 bits.');
    }
    const cipher = context.createBlockCipher({ cipher: 'AES', key });
    return {
        createSealer({ nonce, aad, tagLength }) {
            const resolvedNonce = requireNonce$2(nonce);
            const resolvedAad = resolveAad$2(aad);
            const resolvedTagLength = resolveTagLength$1(tagLength);
            let pendings = [];
            let finalized = false;
            return {
                process(input) {
                    assertNotFinalized$2(finalized);
                    assertBytes(input, 'AES-CCM input');
                    pendings.push(input);
                    return new Uint8Array(0);
                },
                finalize(input = new Uint8Array(0)) {
                    assertNotFinalized$2(finalized);
                    if (input.length !== 0) {
                        this.process(input);
                    }
                    finalized = true;
                    const plaintext = collectPending$1(pendings);
                    pendings = [];
                    return seal(cipher, plaintext, resolvedNonce, resolvedAad, resolvedTagLength);
                },
            };
        },
        createOpener({ nonce, aad, tag, tagLength }) {
            const resolvedNonce = requireNonce$2(nonce);
            const resolvedAad = resolveAad$2(aad);
            const detachedTag = resolveOptionalTag$2(tag);
            const resolvedTagLength = detachedTag
                ? resolveTagLength$1(detachedTag.length)
                : resolveTagLength$1(tagLength);
            let pendings = [];
            let finalized = false;
            return {
                process(input) {
                    assertNotFinalized$2(finalized);
                    assertBytes(input, 'AES-CCM input');
                    pendings.push(input);
                    return new Uint8Array(0);
                },
                finalize(input = new Uint8Array(0)) {
                    assertNotFinalized$2(finalized);
                    if (input.length !== 0) {
                        this.process(input);
                    }
                    finalized = true;
                    const buffered = collectPending$1(pendings);
                    pendings = [];
                    return open(cipher, buffered, resolvedNonce, resolvedAad, resolvedTagLength, detachedTag);
                },
            };
        },
    };
}
function seal(cipher, plaintext, nonce, aad, tagLength) {
    const L = 15 - nonce.length;
    assertPlaintextLength(plaintext.length, L);
    const tag = computeTag$1(cipher, plaintext, nonce, aad, tagLength, L);
    const ciphertext = ctrCrypt(cipher, plaintext, nonce, L);
    const sealed = new Uint8Array(ciphertext.length + tagLength);
    sealed.set(ciphertext, 0);
    sealed.set(tag, ciphertext.length);
    return sealed;
}
function open(cipher, input, nonce, aad, tagLength, detachedTag) {
    const L = 15 - nonce.length;
    let ciphertext;
    let tag;
    if (detachedTag) {
        ciphertext = input;
        tag = detachedTag;
    }
    else {
        if (input.length < tagLength) {
            throw new Error('AES-CCM ciphertext is shorter than the authentication tag.');
        }
        ciphertext = input.subarray(0, input.length - tagLength);
        tag = input.subarray(input.length - tagLength);
    }
    assertPlaintextLength(ciphertext.length, L);
    const plaintext = ctrCrypt(cipher, ciphertext, nonce, L);
    const expectedTag = computeTag$1(cipher, plaintext, nonce, aad, tagLength, L);
    if (!equalBytes$1(tag, expectedTag)) {
        plaintext.fill(0);
        throw new Error('AES-CCM authentication failed.');
    }
    return plaintext;
}
function computeTag$1(cipher, plaintext, nonce, aad, tagLength, L) {
    const mac = new Uint8Array(BLOCK_SIZE$1);
    const block = new Uint8Array(BLOCK_SIZE$1);
    buildB0(block, nonce, plaintext.length, aad.length > 0, tagLength, L);
    encryptBlock(cipher, block, 0, mac, 0);
    if (aad.length > 0) {
        const encodedAad = encodeCcmAad(aad);
        cbcMacBlocks(cipher, mac, encodedAad);
    }
    if (plaintext.length > 0) {
        cbcMacBlocks(cipher, mac, plaintext);
    }
    const s0 = new Uint8Array(BLOCK_SIZE$1);
    buildCounterBlock(block, nonce, 0, L);
    encryptBlock(cipher, block, 0, s0, 0);
    const tag = new Uint8Array(tagLength);
    for (let i = 0; i < tagLength; i++) {
        tag[i] = mac[i] ^ s0[i];
    }
    return tag;
}
function ctrCrypt(cipher, input, nonce, L) {
    if (input.length === 0) {
        return new Uint8Array(0);
    }
    const output = new Uint8Array(input.length);
    const counterBlock = new Uint8Array(BLOCK_SIZE$1);
    const keystream = new Uint8Array(BLOCK_SIZE$1);
    let counter = 1;
    let offset = 0;
    while (offset < input.length) {
        buildCounterBlock(counterBlock, nonce, counter, L);
        encryptBlock(cipher, counterBlock, 0, keystream, 0);
        const n = Math.min(BLOCK_SIZE$1, input.length - offset);
        for (let i = 0; i < n; i++) {
            output[offset + i] = input[offset + i] ^ keystream[i];
        }
        offset += n;
        counter += 1;
    }
    return output;
}
function cbcMacBlocks(cipher, mac, data) {
    const block = new Uint8Array(BLOCK_SIZE$1);
    let offset = 0;
    while (offset < data.length) {
        const n = Math.min(BLOCK_SIZE$1, data.length - offset);
        block.fill(0);
        block.set(data.subarray(offset, offset + n));
        for (let i = 0; i < BLOCK_SIZE$1; i++) {
            block[i] ^= mac[i];
        }
        encryptBlock(cipher, block, 0, mac, 0);
        offset += n;
    }
}
function buildB0(out, nonce, messageLength, hasAad, tagLength, L) {
    out[0] = ((hasAad ? 1 : 0) << 6) | (((tagLength - 2) / 2) << 3) | (L - 1);
    out.set(nonce, 1);
    encodeCcmBinaryLength(messageLength, L, out, 1 + nonce.length);
}
function buildCounterBlock(out, nonce, counter, L) {
    out.fill(0);
    out[0] = L - 1;
    out.set(nonce, 1);
    encodeCcmBinaryLength(counter, L, out, 1 + nonce.length);
}
function encryptBlock(cipher, input, inputOffset, output, outputOffset) {
    if (cipher.encryptBlock) {
        cipher.encryptBlock(input, inputOffset, output, outputOffset);
        return;
    }
    cipher.encrypt(input.subarray(inputOffset, inputOffset + cipher.blockSize), output.subarray(outputOffset, outputOffset + cipher.blockSize));
}
function assertPlaintextLength(length, L) {
    let remaining = length;
    for (let i = 0; i < L; i++) {
        remaining = Math.floor(remaining / 256);
    }
    if (remaining !== 0) {
        throw new RangeError('AES-CCM plaintext is too large for the nonce length.');
    }
}
function requireNonce$2(nonce) {
    if (nonce === undefined) {
        throw new Error('AES-CCM requires a nonce.');
    }
    assertBytes(nonce, 'AES-CCM nonce');
    if (nonce.length < MIN_NONCE_LENGTH || nonce.length > MAX_NONCE_LENGTH) {
        throw new RangeError('AES-CCM nonce length must be between 7 and 13 bytes.');
    }
    return nonce;
}
function resolveAad$2(aad) {
    if (aad === undefined) {
        return new Uint8Array(0);
    }
    assertBytes(aad, 'AES-CCM aad');
    return aad;
}
function resolveOptionalTag$2(tag) {
    if (tag === undefined) {
        return undefined;
    }
    assertBytes(tag, 'AES-CCM tag');
    resolveTagLength$1(tag.length);
    return tag;
}
function resolveTagLength$1(tagLength) {
    if (tagLength === undefined) {
        return DEFAULT_TAG_LENGTH$1;
    }
    if (typeof tagLength !== 'number'
        || !Number.isInteger(tagLength)
        || TAG_SIZES$1.indexOf(tagLength) === -1) {
        throw new RangeError('AES-CCM tagLength must be one of 4, 6, 8, 10, 12, 14, or 16 bytes.');
    }
    return tagLength;
}
function collectPending$1(pendings) {
    if (pendings.length === 0) {
        return new Uint8Array(0);
    }
    if (pendings.length === 1) {
        return pendings[0];
    }
    return concatBytes(...pendings);
}
function assertNotFinalized$2(finalized) {
    if (finalized) {
        throw new Error('AES-CCM transform already finalized.');
    }
}

const KEY_SIZES = [16, 24, 32];
const TAG_SIZES = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const DEFAULT_TAG_LENGTH = 16;
const MIN_TAG_LENGTH = 4;
const MAX_TAG_LENGTH = 16;
function createAesGcmComponent() {
    return {
        kind: 'aead',
        name: 'AES-GCM',
        keySizes: KEY_SIZES,
        recommendedNonceSize: 12,
        tagSizes: TAG_SIZES,
        create(params, context) {
            return createAesGcmTransform(params, context);
        },
    };
}
function createAesGcmTransform({ key }, context) {
    assertBytes(key, 'AES-GCM key');
    return {
        createSealer({ nonce, aad, tagLength, options }) {
            return context.createEncryptor(toGcmOptions(key, {
                nonce: requireNonce$1(nonce),
                aad: resolveAad$1(aad),
                tagLength: resolveTagLength(tagLength),
                options,
            }));
        },
        createOpener({ nonce, aad, tag, tagLength, options }) {
            const detachedTag = resolveOptionalTag$1(tag);
            return wrapGcmOpener(context.createDecryptor(toGcmOptions(key, {
                nonce: requireNonce$1(nonce),
                aad: resolveAad$1(aad),
                tag: detachedTag,
                tagLength: detachedTag ? resolveTagLength(detachedTag.length) : resolveTagLength(tagLength),
                options,
            })));
        },
    };
}
function toGcmOptions(key, { nonce, aad, tag, tagLength, options, }) {
    return {
        ...toRecord(options),
        cipher: 'AES',
        mode: 'GCM',
        key,
        nonce,
        aad,
        tag,
        tagLength,
    };
}
function wrapGcmOpener(opener) {
    return {
        process(input) {
            return opener.process(input);
        },
        finalize(input) {
            try {
                return opener.finalize(input);
            }
            catch (error) {
                if (error instanceof Error && error.message === 'GCM authentication failed.') {
                    throw new Error('AES-GCM authentication failed.');
                }
                throw error;
            }
        },
    };
}
function requireNonce$1(nonce) {
    if (nonce === undefined) {
        throw new Error('AES-GCM requires a nonce.');
    }
    assertBytes(nonce, 'AES-GCM nonce');
    if (nonce.length === 0) {
        throw new Error('AES-GCM requires a nonce.');
    }
    return nonce;
}
function resolveAad$1(aad) {
    if (aad === undefined) {
        return new Uint8Array(0);
    }
    assertBytes(aad, 'AES-GCM aad');
    return aad;
}
function resolveOptionalTag$1(tag) {
    if (tag === undefined) {
        return undefined;
    }
    assertBytes(tag, 'AES-GCM tag');
    return tag;
}
function resolveTagLength(tagLength) {
    if (tagLength === undefined) {
        return DEFAULT_TAG_LENGTH;
    }
    if (typeof tagLength !== 'number' ||
        !Number.isInteger(tagLength) ||
        tagLength < MIN_TAG_LENGTH ||
        tagLength > MAX_TAG_LENGTH) {
        throw new RangeError('AES-GCM tagLength must be an integer between 4 and 16 bytes.');
    }
    return tagLength;
}
function toRecord(options) {
    return typeof options === 'object' && options !== null ? options : {};
}

const BLOCK_SIZE = 16;
const aes = {
    kind: 'cipher',
    name: 'AES',
    type: 'block',
    blockSize: BLOCK_SIZE,
    keySizes: [16, 24, 32],
    create(key) {
        return createAesCipher(key);
    },
};
const aesGcm = createAesGcmComponent();
const aesCcm = createAesCcmComponent();
const aesPreset = {
    kind: 'preset',
    name: 'aes',
    components() {
        return [aes, aesGcm, aesCcm];
    },
};
function createAesCipher(key) {
    if (key.length !== 16 && key.length !== 24 && key.length !== 32) {
        throw new Error('AES key must be 128, 192, or 256 bits.');
    }
    const encryptionKey = unsafe.expandKeyLE(key);
    const decryptionKey = unsafe.expandKeyDecLE(key);
    return {
        blockSize: BLOCK_SIZE,
        encryptBlock(input, inputOffset, output, outputOffset) {
            transformBlock$1(encryptionKey, input, inputOffset, output, outputOffset, unsafe.encrypt);
        },
        decryptBlock(input, inputOffset, output, outputOffset) {
            transformBlock$1(decryptionKey, input, inputOffset, output, outputOffset, unsafe.decrypt);
        },
        encrypt(input, output) {
            assertBlocks$3(input, output);
            return transformBlocks$1(encryptionKey, input, output, unsafe.encrypt);
        },
        decrypt(input, output) {
            assertBlocks$3(input, output);
            return transformBlocks$1(decryptionKey, input, output, unsafe.decrypt);
        },
    };
}
function transformBlocks$1(expandedKey, input, output, transform) {
    for (let offset = 0; offset < input.length; offset += BLOCK_SIZE) {
        transformBlock$1(expandedKey, input, offset, output, offset, transform);
    }
    return output;
}
function transformBlock$1(expandedKey, input, inputOffset, output, outputOffset, transform) {
    const { s0, s1, s2, s3 } = transform(expandedKey, readUint32LE(input, inputOffset), readUint32LE(input, inputOffset + 4), readUint32LE(input, inputOffset + 8), readUint32LE(input, inputOffset + 12));
    writeUint32LE(output, outputOffset, s0);
    writeUint32LE(output, outputOffset + 4, s1);
    writeUint32LE(output, outputOffset + 8, s2);
    writeUint32LE(output, outputOffset + 12, s3);
}
function readUint32LE(input, offset) {
    return (input[offset]
        | (input[offset + 1] << 8)
        | (input[offset + 2] << 16)
        | (input[offset + 3] << 24)) >>> 0;
}
function writeUint32LE(output, offset, value) {
    output[offset] = value;
    output[offset + 1] = value >>> 8;
    output[offset + 2] = value >>> 16;
    output[offset + 3] = value >>> 24;
}
function assertBlocks$3(input, output) {
    if (input.length % BLOCK_SIZE !== 0) {
        throw new Error('AES input length must be a multiple of 128 bits.');
    }
    if (output.length !== input.length) {
        throw new Error('AES output length must equal input length.');
    }
}

/**
 * Basic utils for ARX (add-rotate-xor) salsa and chacha ciphers.

RFC8439 requires multi-step cipher stream, where
authKey starts with counter: 0, actual msg with counter: 1.

For this, we need a way to re-use nonce / counter:

    const counter = new Uint8Array(4);
    chacha(..., counter, ...); // counter is now 1
    chacha(..., counter, ...); // counter is now 2

This is complicated:

- 32-bit counters are enough, no need for 64-bit: max ArrayBuffer size in JS is 4GB
- Original papers don't allow mutating counters
- Counter overflow is undefined [^1]
- Idea A: allow providing (nonce | counter) instead of just nonce, re-use it
- Caveat: Cannot be re-used through all cases:
- * chacha has (counter | nonce)
- * xchacha has (nonce16 | counter | nonce16)
- Idea B: separate nonce / counter and provide separate API for counter re-use
- Caveat: there are different counter sizes depending on an algorithm.
- salsa & chacha also differ in structures of key & sigma:
  salsa20:      s[0] | k(4) | s[1] | nonce(2) | cnt(2) | s[2] | k(4) | s[3]
  chacha:       s(4) | k(8) | cnt(1) | nonce(3)
  chacha20orig: s(4) | k(8) | cnt(2) | nonce(2)
- Idea C: helper method such as `setSalsaState(key, nonce, sigma, data)`
- Caveat: we can't re-use counter array

xchacha uses the subkey and remaining 8 byte nonce with ChaCha20 as normal
(prefixed by 4 NUL bytes, since RFC8439 specifies a 12-byte nonce).
Counter overflow is undefined; see {@link https://mailarchive.ietf.org/arch/msg/cfrg/gsOnTJzcbgG6OqD8Sc0GO5aR_tU/ | the CFRG thread}.
Current noble policy is strict non-wrap for the shared 32-bit counter path:
exported ARX ciphers reject initial `0xffffffff` and stop before any implicit
wrap back to zero.
See {@link https://datatracker.ietf.org/doc/html/draft-irtf-cfrg-xchacha#appendix-A.2 | the XChaCha appendix} for the extended-nonce construction.

 * @module
 */
// Replaces `TextEncoder` for ASCII literals, which is enough for sigma constants.
// Non-ASCII input would not match UTF-8 `TextEncoder` output.
const encodeStr = (str) => Uint8Array.from(str.split(''), (c) => c.charCodeAt(0));
// Raw `createCipher(...)` exports consume these native-endian `u32(...)` views directly.
// Public `wrapCipher(...)` APIs reject non-little-endian platforms before reaching this path.
// RFC 8439 §2.3 / RFC 7539 §2.3 only define the 256-bit-key constants; this 16-byte sigma is
// kept for legacy allowShortKeys Salsa/ChaCha variants.
const sigma16_32 = /* @__PURE__ */ (() => swap32IfBE(u32(encodeStr('expand 16-byte k'))))();
// RFC 8439 §2.3 / RFC 7539 §2.3 define words 0-3 as
// `0x61707865 0x3320646e 0x79622d32 0x6b206574`, i.e. `expand 32-byte k`.
const sigma32_32 = /* @__PURE__ */ (() => swap32IfBE(u32(encodeStr('expand 32-byte k'))))();
/**
 * Rotates a 32-bit word left.
 * @param a - Input word.
 * @param b - Rotation count in bits.
 * @returns Rotated 32-bit word.
 * @example
 * Moves the top byte of `0x12345678` into the low byte position.
 * ```ts
 * rotl(0x12345678, 8);
 * ```
 */
function rotl$1(a, b) {
    return (a << b) | (a >>> (32 - b));
}
// Salsa and Chacha block length is always 512-bit
const BLOCK_LEN = 64;
// RFC 8439 §2.2 / RFC 7539 §2.2: the ChaCha state has 16 32-bit words.
const BLOCK_LEN32 = 16;
// Counter policy for the shared public `counter` argument:
// - RFC/IETF ChaCha20 uses a 32-bit counter.
// - OpenSSL/Node `chacha20` instead treat the full 16-byte IV as a 128-bit
//   counter state and carry into the next word.
// - Raw `chacha20orig`, `salsa20`, `xsalsa20`, and `xchacha20` use 64-bit counters in libsodium
//   and libtomcrypt, while some libs (for example libtomcrypt's RFC/IETF path) reject the max
//   boundary instead of carrying.
// - AEAD wrappers diverge too: libsodium `xchacha20poly1305` uses the IETF payload counter from
//   block 1, while `secretstream_xchacha20poly1305` is a different protocol with rekey/reset.
// Noble intentionally throws instead of silently picking one wrap model for users. In the default
// path, even a 32-bit boundary would take 2^32 blocks * 64 bytes = 256 GiB, which is practically
// unreachable for normal JS callers; advanced users who pass `counter` explicitly can implement
// whatever wider carry / wrap policy they need on top.
const MAX_COUNTER$1 = /* @__PURE__ */ (() => 2 ** 32 - 1)();
const U32_EMPTY = /* @__PURE__ */ Uint32Array.of();
function runCipher(core, sigma, key, nonce, data, output, counter, rounds) {
    const len = data.length;
    const block = new Uint8Array(BLOCK_LEN);
    const b32 = u32(block);
    // Make sure that buffers aligned to 4 bytes
    const isAligned = isLE && isAligned32(data) && isAligned32(output);
    const d32 = isAligned ? u32(data) : U32_EMPTY;
    const o32 = isAligned ? u32(output) : U32_EMPTY;
    // RFC 8439 §2.4.1 / RFC 7539 §2.4.1 allow XORing one keystream block at a time and
    // truncating the final partial block instead of materializing the whole keystream.
    if (!isLE) {
        for (let pos = 0; pos < len; counter++) {
            core(sigma, key, nonce, b32, counter, rounds);
            // RFC 8439 §2.4 / RFC 7539 §2.4 serialize keystream words in little-endian order.
            swap32IfBE(b32);
            if (counter >= MAX_COUNTER$1)
                throw new Error('arx: counter overflow');
            const take = Math.min(BLOCK_LEN, len - pos);
            for (let j = 0, posj; j < take; j++) {
                posj = pos + j;
                output[posj] = data[posj] ^ block[j];
            }
            pos += take;
        }
        return;
    }
    for (let pos = 0; pos < len; counter++) {
        core(sigma, key, nonce, b32, counter, rounds);
        // See MAX_COUNTER policy note above: never silently wrap the shared public counter.
        if (counter >= MAX_COUNTER$1)
            throw new Error('arx: counter overflow');
        const take = Math.min(BLOCK_LEN, len - pos);
        // aligned to 4 bytes
        if (isAligned && take === BLOCK_LEN) {
            const pos32 = pos / 4;
            if (pos % 4 !== 0)
                throw new Error('arx: invalid block position');
            for (let j = 0, posj; j < BLOCK_LEN32; j++) {
                posj = pos32 + j;
                o32[posj] = d32[posj] ^ b32[j];
            }
            pos += BLOCK_LEN;
            continue;
        }
        for (let j = 0, posj; j < take; j++) {
            posj = pos + j;
            output[posj] = data[posj] ^ block[j];
        }
        pos += take;
    }
}
/**
 * Creates an ARX stream cipher from a 32-bit core permutation.
 * Used internally to build the exported Salsa and ChaCha stream ciphers.
 * @param core - Core function that fills one keystream block.
 * @param opts - Cipher layout and nonce-extension options. See {@link CipherOpts}.
 * @returns Stream cipher function over byte arrays.
 * @throws If the core callback, key size, counter, or output sizing is invalid. {@link Error}
 */
function createCipher(core, opts) {
    const { allowShortKeys, extendNonceFn, counterLength, counterRight, rounds } = checkOpts({ allowShortKeys: false, counterLength: 8, counterRight: false, rounds: 20 }, opts);
    if (typeof core !== 'function')
        throw new Error('core must be a function');
    anumber(counterLength);
    anumber(rounds);
    abool(counterRight);
    abool(allowShortKeys);
    return (key, nonce, data, output, counter = 0) => {
        abytes(key, undefined, 'key');
        abytes(nonce, undefined, 'nonce');
        abytes(data, undefined, 'data');
        const len = data.length;
        // Raw XorStream APIs return ciphertext/plaintext bytes directly, so caller-provided outputs
        // must match the logical result length exactly instead of returning an oversized workspace.
        output = getOutput(len, output, false);
        anumber(counter);
        // See MAX_COUNTER policy note above: reject advanced explicit-counter requests before any wrap.
        if (counter < 0 || counter >= MAX_COUNTER$1)
            throw new Error('arx: counter overflow');
        const toClean = [];
        // Key & sigma
        // key=16 -> sigma16, k=key|key
        // key=32 -> sigma32, k=key
        let l = key.length;
        let k;
        let sigma;
        if (l === 32) {
            // Copy caller keys too: big-endian normalization, extended-nonce subkey derivation, and
            // final clean(...) all mutate or wipe the temporary buffer in place.
            toClean.push((k = copyBytes(key)));
            sigma = sigma32_32;
        }
        else if (l === 16 && allowShortKeys) {
            k = new Uint8Array(32);
            k.set(key);
            k.set(key, 16);
            sigma = sigma16_32;
            toClean.push(k);
        }
        else {
            abytes(key, 32, 'arx key');
            throw new Error('invalid key size');
            // throw new Error(`"arx key" expected Uint8Array of length 32, got length=${l}`);
        }
        // Nonce
        // salsa20:      8   (8-byte counter)
        // chacha20orig: 8   (8-byte counter)
        // chacha20:     12  (4-byte counter)
        // xsalsa20:     24  (16 -> hsalsa,  8 -> old nonce)
        // xchacha20:    24  (16 -> hchacha, 8 -> old nonce)
        // Copy before taking u32(...) views on misaligned inputs, and on big-endian so later
        // swap32IfBE(...) never mutates caller nonce bytes in place.
        if (!isLE || !isAligned32(nonce))
            toClean.push((nonce = copyBytes(nonce)));
        let k32 = u32(k);
        // hsalsa & hchacha: handle extended nonce
        if (extendNonceFn) {
            if (nonce.length !== 24)
                throw new Error(`arx: extended nonce must be 24 bytes`);
            const n16 = nonce.subarray(0, 16);
            if (isLE)
                extendNonceFn(sigma, k32, u32(n16), k32);
            else {
                const sigmaRaw = swap32IfBE(Uint32Array.from(sigma));
                extendNonceFn(sigmaRaw, k32, u32(n16), k32);
                clean(sigmaRaw);
                swap32IfBE(k32);
            }
            nonce = nonce.subarray(16);
        }
        else if (!isLE)
            swap32IfBE(k32);
        // Handle nonce counter
        const nonceNcLen = 16 - counterLength;
        if (nonceNcLen !== nonce.length)
            throw new Error(`arx: nonce must be ${nonceNcLen} or 16 bytes`);
        // Normalize 64-bit-nonce layouts to the 12-byte core input: ChaCha/XChaCha prefix 4 zero
        // counter bytes, while Salsa/XSalsa append them after the nonce words.
        if (nonceNcLen !== 12) {
            const nc = new Uint8Array(12);
            nc.set(nonce, counterRight ? 0 : 12 - nonce.length);
            nonce = nc;
            toClean.push(nonce);
        }
        const n32 = swap32IfBE(u32(nonce));
        // Ensure temporary key/nonce copies are wiped even if the remaining
        // runtime guard in runCipher(...) throws on counter overflow.
        try {
            runCipher(core, sigma, k32, n32, data, output, counter, rounds);
            return output;
        }
        finally {
            clean(...toClean);
        }
    };
}

/**
 * Poly1305 ({@link https://cr.yp.to/mac/poly1305-20050329.pdf | PDF},
 * {@link https://en.wikipedia.org/wiki/Poly1305 | wiki})
 * is a fast and parallel secret-key message-authentication code suitable for
 * a wide variety of applications. It was standardized in
 * {@link https://www.rfc-editor.org/rfc/rfc8439 | RFC 8439} and is now used in TLS 1.3.
 *
 * Polynomial MACs are not perfect for every situation:
 * they lack Random Key Robustness: the MAC can be forged, and can't be used in PAKE schemes.
 * See {@link https://keymaterial.net/2020/09/07/invisible-salamanders-in-aes-gcm-siv/ | the invisible salamanders attack writeup}.
 * To combat invisible salamanders, `hash(key)` can be included in ciphertext,
 * however, this would violate ciphertext indistinguishability:
 * an attacker would know which key was used - so `HKDF(key, i)`
 * could be used instead.
 *
 * Check out the {@link https://cr.yp.to/mac.html | original website}.
 * Based on public-domain {@link https://github.com/floodyberry/poly1305-donna | poly1305-donna}.
 * @module
 */
// prettier-ignore
// Little-endian 2-byte load used by the Poly1305 limb decomposition.
function u8to16(a, i) {
    return (a[i++] & 0xff) | ((a[i++] & 0xff) << 8);
}
/**
 * Incremental Poly1305 MAC state.
 * Prefer `poly1305()` for one-shot use.
 * @param key - 32-byte Poly1305 one-time key.
 * @example
 * Feeds one chunk into an incremental Poly1305 state with a fresh one-time key.
 *
 * ```ts
 * import { Poly1305 } from '@noble/ciphers/_poly1305.js';
 * import { randomBytes } from '@noble/ciphers/utils.js';
 * const key = randomBytes(32);
 * const mac = new Poly1305(key);
 * mac.update(new Uint8Array([1, 2, 3]));
 * mac.digest();
 * ```
 */
class Poly1305 {
    blockLen = 16;
    outputLen = 16;
    buffer = new Uint8Array(16);
    r = new Uint16Array(10); // Allocating 1 array with .subarray() here is slower than 3
    h = new Uint16Array(10);
    pad = new Uint16Array(8);
    pos = 0;
    finished = false;
    destroyed = false;
    // Can be speed-up using BigUint64Array, at the cost of complexity
    constructor(key) {
        key = copyBytes(abytes(key, 32, 'key'));
        const t0 = u8to16(key, 0);
        const t1 = u8to16(key, 2);
        const t2 = u8to16(key, 4);
        const t3 = u8to16(key, 6);
        const t4 = u8to16(key, 8);
        const t5 = u8to16(key, 10);
        const t6 = u8to16(key, 12);
        const t7 = u8to16(key, 14);
        // RFC 8439 §2.5.1 / RFC 7539 §2.5.1 clamp r before multiplication.
        // These masks unpack that clamped value into 13-bit limbs, while pad
        // keeps the raw s half for finalize().
        // {@link https://github.com/floodyberry/poly1305-donna/blob/e6ad6e091d30d7f4ec2d4f978be1fcfcbce72781/poly1305-donna-16.h#L47 | poly1305-donna reference}
        this.r[0] = t0 & 0x1fff;
        this.r[1] = ((t0 >>> 13) | (t1 << 3)) & 0x1fff;
        this.r[2] = ((t1 >>> 10) | (t2 << 6)) & 0x1f03;
        this.r[3] = ((t2 >>> 7) | (t3 << 9)) & 0x1fff;
        this.r[4] = ((t3 >>> 4) | (t4 << 12)) & 0x00ff;
        this.r[5] = (t4 >>> 1) & 0x1ffe;
        this.r[6] = ((t4 >>> 14) | (t5 << 2)) & 0x1fff;
        this.r[7] = ((t5 >>> 11) | (t6 << 5)) & 0x1f81;
        this.r[8] = ((t6 >>> 8) | (t7 << 8)) & 0x1fff;
        this.r[9] = (t7 >>> 5) & 0x007f;
        for (let i = 0; i < 8; i++)
            this.pad[i] = u8to16(key, 16 + 2 * i);
    }
    process(data, offset, isLast = false) {
        // RFC 8439 §2.5 / §2.5.1 and RFC 7539 §2.5 / §2.5.1 add an extra high
        // bit to every full 16-byte block. The final partial block gets its
        // explicit `1` byte during digestInto(), so `hibit` stays zero there.
        const hibit = isLast ? 0 : 1 << 11;
        const { h, r } = this;
        const r0 = r[0];
        const r1 = r[1];
        const r2 = r[2];
        const r3 = r[3];
        const r4 = r[4];
        const r5 = r[5];
        const r6 = r[6];
        const r7 = r[7];
        const r8 = r[8];
        const r9 = r[9];
        const t0 = u8to16(data, offset + 0);
        const t1 = u8to16(data, offset + 2);
        const t2 = u8to16(data, offset + 4);
        const t3 = u8to16(data, offset + 6);
        const t4 = u8to16(data, offset + 8);
        const t5 = u8to16(data, offset + 10);
        const t6 = u8to16(data, offset + 12);
        const t7 = u8to16(data, offset + 14);
        let h0 = h[0] + (t0 & 0x1fff);
        let h1 = h[1] + (((t0 >>> 13) | (t1 << 3)) & 0x1fff);
        let h2 = h[2] + (((t1 >>> 10) | (t2 << 6)) & 0x1fff);
        let h3 = h[3] + (((t2 >>> 7) | (t3 << 9)) & 0x1fff);
        let h4 = h[4] + (((t3 >>> 4) | (t4 << 12)) & 0x1fff);
        let h5 = h[5] + ((t4 >>> 1) & 0x1fff);
        let h6 = h[6] + (((t4 >>> 14) | (t5 << 2)) & 0x1fff);
        let h7 = h[7] + (((t5 >>> 11) | (t6 << 5)) & 0x1fff);
        let h8 = h[8] + (((t6 >>> 8) | (t7 << 8)) & 0x1fff);
        let h9 = h[9] + ((t7 >>> 5) | hibit);
        let c = 0;
        let d0 = c + h0 * r0 + h1 * (5 * r9) + h2 * (5 * r8) + h3 * (5 * r7) + h4 * (5 * r6);
        c = d0 >>> 13;
        d0 &= 0x1fff;
        d0 += h5 * (5 * r5) + h6 * (5 * r4) + h7 * (5 * r3) + h8 * (5 * r2) + h9 * (5 * r1);
        c += d0 >>> 13;
        d0 &= 0x1fff;
        let d1 = c + h0 * r1 + h1 * r0 + h2 * (5 * r9) + h3 * (5 * r8) + h4 * (5 * r7);
        c = d1 >>> 13;
        d1 &= 0x1fff;
        d1 += h5 * (5 * r6) + h6 * (5 * r5) + h7 * (5 * r4) + h8 * (5 * r3) + h9 * (5 * r2);
        c += d1 >>> 13;
        d1 &= 0x1fff;
        let d2 = c + h0 * r2 + h1 * r1 + h2 * r0 + h3 * (5 * r9) + h4 * (5 * r8);
        c = d2 >>> 13;
        d2 &= 0x1fff;
        d2 += h5 * (5 * r7) + h6 * (5 * r6) + h7 * (5 * r5) + h8 * (5 * r4) + h9 * (5 * r3);
        c += d2 >>> 13;
        d2 &= 0x1fff;
        let d3 = c + h0 * r3 + h1 * r2 + h2 * r1 + h3 * r0 + h4 * (5 * r9);
        c = d3 >>> 13;
        d3 &= 0x1fff;
        d3 += h5 * (5 * r8) + h6 * (5 * r7) + h7 * (5 * r6) + h8 * (5 * r5) + h9 * (5 * r4);
        c += d3 >>> 13;
        d3 &= 0x1fff;
        let d4 = c + h0 * r4 + h1 * r3 + h2 * r2 + h3 * r1 + h4 * r0;
        c = d4 >>> 13;
        d4 &= 0x1fff;
        d4 += h5 * (5 * r9) + h6 * (5 * r8) + h7 * (5 * r7) + h8 * (5 * r6) + h9 * (5 * r5);
        c += d4 >>> 13;
        d4 &= 0x1fff;
        let d5 = c + h0 * r5 + h1 * r4 + h2 * r3 + h3 * r2 + h4 * r1;
        c = d5 >>> 13;
        d5 &= 0x1fff;
        d5 += h5 * r0 + h6 * (5 * r9) + h7 * (5 * r8) + h8 * (5 * r7) + h9 * (5 * r6);
        c += d5 >>> 13;
        d5 &= 0x1fff;
        let d6 = c + h0 * r6 + h1 * r5 + h2 * r4 + h3 * r3 + h4 * r2;
        c = d6 >>> 13;
        d6 &= 0x1fff;
        d6 += h5 * r1 + h6 * r0 + h7 * (5 * r9) + h8 * (5 * r8) + h9 * (5 * r7);
        c += d6 >>> 13;
        d6 &= 0x1fff;
        let d7 = c + h0 * r7 + h1 * r6 + h2 * r5 + h3 * r4 + h4 * r3;
        c = d7 >>> 13;
        d7 &= 0x1fff;
        d7 += h5 * r2 + h6 * r1 + h7 * r0 + h8 * (5 * r9) + h9 * (5 * r8);
        c += d7 >>> 13;
        d7 &= 0x1fff;
        let d8 = c + h0 * r8 + h1 * r7 + h2 * r6 + h3 * r5 + h4 * r4;
        c = d8 >>> 13;
        d8 &= 0x1fff;
        d8 += h5 * r3 + h6 * r2 + h7 * r1 + h8 * r0 + h9 * (5 * r9);
        c += d8 >>> 13;
        d8 &= 0x1fff;
        let d9 = c + h0 * r9 + h1 * r8 + h2 * r7 + h3 * r6 + h4 * r5;
        c = d9 >>> 13;
        d9 &= 0x1fff;
        d9 += h5 * r4 + h6 * r3 + h7 * r2 + h8 * r1 + h9 * r0;
        c += d9 >>> 13;
        d9 &= 0x1fff;
        c = ((c << 2) + c) | 0;
        c = (c + d0) | 0;
        d0 = c & 0x1fff;
        c = c >>> 13;
        d1 += c;
        h[0] = d0;
        h[1] = d1;
        h[2] = d2;
        h[3] = d3;
        h[4] = d4;
        h[5] = d5;
        h[6] = d6;
        h[7] = d7;
        h[8] = d8;
        h[9] = d9;
    }
    finalize() {
        const { h, pad } = this;
        const g = new Uint16Array(10);
        let c = h[1] >>> 13;
        h[1] &= 0x1fff;
        for (let i = 2; i < 10; i++) {
            h[i] += c;
            c = h[i] >>> 13;
            h[i] &= 0x1fff;
        }
        h[0] += c * 5;
        c = h[0] >>> 13;
        h[0] &= 0x1fff;
        h[1] += c;
        c = h[1] >>> 13;
        h[1] &= 0x1fff;
        h[2] += c;
        // RFC 8439 §2.5 / RFC 7539 §2.5 reduce modulo 2^130-5 before repacking
        // to 16-bit words and adding the raw s half.
        g[0] = h[0] + 5;
        c = g[0] >>> 13;
        g[0] &= 0x1fff;
        for (let i = 1; i < 10; i++) {
            g[i] = h[i] + c;
            c = g[i] >>> 13;
            g[i] &= 0x1fff;
        }
        g[9] -= 1 << 13;
        let mask = (c ^ 1) - 1;
        for (let i = 0; i < 10; i++)
            g[i] &= mask;
        mask = ~mask;
        for (let i = 0; i < 10; i++)
            h[i] = (h[i] & mask) | g[i];
        h[0] = (h[0] | (h[1] << 13)) & 0xffff;
        h[1] = ((h[1] >>> 3) | (h[2] << 10)) & 0xffff;
        h[2] = ((h[2] >>> 6) | (h[3] << 7)) & 0xffff;
        h[3] = ((h[3] >>> 9) | (h[4] << 4)) & 0xffff;
        h[4] = ((h[4] >>> 12) | (h[5] << 1) | (h[6] << 14)) & 0xffff;
        h[5] = ((h[6] >>> 2) | (h[7] << 11)) & 0xffff;
        h[6] = ((h[7] >>> 5) | (h[8] << 8)) & 0xffff;
        h[7] = ((h[8] >>> 8) | (h[9] << 5)) & 0xffff;
        let f = h[0] + pad[0];
        h[0] = f & 0xffff;
        for (let i = 1; i < 8; i++) {
            f = (((h[i] + pad[i]) | 0) + (f >>> 16)) | 0;
            h[i] = f & 0xffff;
        }
        clean(g);
    }
    update(data) {
        aexists(this);
        abytes(data);
        data = copyBytes(data);
        const { buffer, blockLen } = this;
        const len = data.length;
        for (let pos = 0; pos < len;) {
            const take = Math.min(blockLen - this.pos, len - pos);
            // Fast path: we have at least one block in input
            if (take === blockLen) {
                for (; blockLen <= len - pos; pos += blockLen)
                    this.process(data, pos);
                continue;
            }
            buffer.set(data.subarray(pos, pos + take), this.pos);
            this.pos += take;
            pos += take;
            if (this.pos === blockLen) {
                this.process(buffer, 0, false);
                this.pos = 0;
            }
        }
        return this;
    }
    destroy() {
        // `aexists(this)` guards update/digest paths, so destroy must mark the instance unusable too.
        this.destroyed = true;
        clean(this.h, this.r, this.buffer, this.pad);
    }
    digestInto(out) {
        aexists(this);
        aoutput(out, this);
        this.finished = true;
        const { buffer, h } = this;
        let { pos } = this;
        if (pos) {
            // RFC 8439 §2.5 / RFC 7539 §2.5: the final short block appends a
            // single `0x01` byte and zero-fills the remaining bytes before the
            // last multiplication step.
            buffer[pos++] = 1;
            for (; pos < 16; pos++)
                buffer[pos] = 0;
            this.process(buffer, 0, true);
        }
        this.finalize();
        let opos = 0;
        for (let i = 0; i < 8; i++) {
            out[opos++] = h[i] >>> 0;
            out[opos++] = h[i] >>> 8;
        }
    }
    digest() {
        const { buffer, outputLen } = this;
        this.digestInto(buffer);
        // Copy out before destroy() zeroes the internal buffer.
        const res = buffer.slice(0, outputLen);
        this.destroy();
        return res;
    }
}
/**
 * Poly1305 MAC from RFC 8439.
 * @param msg - Message bytes to authenticate.
 * @param key - 32-byte Poly1305 one-time key.
 * @returns 16-byte authentication tag.
 * @example
 * Authenticates one message with a one-shot Poly1305 call and a fresh key.
 *
 * ```ts
 * import { poly1305 } from '@noble/ciphers/_poly1305.js';
 * import { randomBytes } from '@noble/ciphers/utils.js';
 * const key = randomBytes(32);
 * poly1305(new Uint8Array(), key);
 * ```
 */
const poly1305 = /* @__PURE__ */ wrapMacConstructor(32, (key) => new Poly1305(key));

/**
 * ChaCha stream cipher, released
 * in 2008. Developed after Salsa20, ChaCha aims to increase diffusion per round.
 * It was standardized in
 * {@link https://www.rfc-editor.org/rfc/rfc8439 | RFC 8439} and
 * is now used in TLS 1.3.
 *
 * {@link https://datatracker.ietf.org/doc/html/draft-irtf-cfrg-xchacha | XChaCha20}
 * extended-nonce variant is also provided. Similar to XSalsa, it's safe to use with
 * randomly-generated nonces.
 *
 * Check out
 * {@link http://cr.yp.to/chacha/chacha-20080128.pdf | PDF},
 * {@link https://en.wikipedia.org/wiki/Salsa20 | wiki}, and
 * {@link https://cr.yp.to/chacha.html | website}.
 *
 * @module
 */
/** RFC 8439 §2.3 block core for `state = constants | key | counter | nonce`. */
// prettier-ignore
function chachaCore(s, k, n, out, cnt, rounds = 20) {
    let y00 = s[0], y01 = s[1], y02 = s[2], y03 = s[3], // "expa"   "nd 3"  "2-by"  "te k"
    y04 = k[0], y05 = k[1], y06 = k[2], y07 = k[3], // Key      Key     Key     Key
    y08 = k[4], y09 = k[5], y10 = k[6], y11 = k[7], // Key      Key     Key     Key
    y12 = cnt, y13 = n[0], y14 = n[1], y15 = n[2]; // Counter  Nonce   Nonce   Nonce
    // Save state to temporary variables
    let x00 = y00, x01 = y01, x02 = y02, x03 = y03, x04 = y04, x05 = y05, x06 = y06, x07 = y07, x08 = y08, x09 = y09, x10 = y10, x11 = y11, x12 = y12, x13 = y13, x14 = y14, x15 = y15;
    for (let r = 0; r < rounds; r += 2) {
        x00 = (x00 + x04) | 0;
        x12 = rotl$1(x12 ^ x00, 16);
        x08 = (x08 + x12) | 0;
        x04 = rotl$1(x04 ^ x08, 12);
        x00 = (x00 + x04) | 0;
        x12 = rotl$1(x12 ^ x00, 8);
        x08 = (x08 + x12) | 0;
        x04 = rotl$1(x04 ^ x08, 7);
        x01 = (x01 + x05) | 0;
        x13 = rotl$1(x13 ^ x01, 16);
        x09 = (x09 + x13) | 0;
        x05 = rotl$1(x05 ^ x09, 12);
        x01 = (x01 + x05) | 0;
        x13 = rotl$1(x13 ^ x01, 8);
        x09 = (x09 + x13) | 0;
        x05 = rotl$1(x05 ^ x09, 7);
        x02 = (x02 + x06) | 0;
        x14 = rotl$1(x14 ^ x02, 16);
        x10 = (x10 + x14) | 0;
        x06 = rotl$1(x06 ^ x10, 12);
        x02 = (x02 + x06) | 0;
        x14 = rotl$1(x14 ^ x02, 8);
        x10 = (x10 + x14) | 0;
        x06 = rotl$1(x06 ^ x10, 7);
        x03 = (x03 + x07) | 0;
        x15 = rotl$1(x15 ^ x03, 16);
        x11 = (x11 + x15) | 0;
        x07 = rotl$1(x07 ^ x11, 12);
        x03 = (x03 + x07) | 0;
        x15 = rotl$1(x15 ^ x03, 8);
        x11 = (x11 + x15) | 0;
        x07 = rotl$1(x07 ^ x11, 7);
        x00 = (x00 + x05) | 0;
        x15 = rotl$1(x15 ^ x00, 16);
        x10 = (x10 + x15) | 0;
        x05 = rotl$1(x05 ^ x10, 12);
        x00 = (x00 + x05) | 0;
        x15 = rotl$1(x15 ^ x00, 8);
        x10 = (x10 + x15) | 0;
        x05 = rotl$1(x05 ^ x10, 7);
        x01 = (x01 + x06) | 0;
        x12 = rotl$1(x12 ^ x01, 16);
        x11 = (x11 + x12) | 0;
        x06 = rotl$1(x06 ^ x11, 12);
        x01 = (x01 + x06) | 0;
        x12 = rotl$1(x12 ^ x01, 8);
        x11 = (x11 + x12) | 0;
        x06 = rotl$1(x06 ^ x11, 7);
        x02 = (x02 + x07) | 0;
        x13 = rotl$1(x13 ^ x02, 16);
        x08 = (x08 + x13) | 0;
        x07 = rotl$1(x07 ^ x08, 12);
        x02 = (x02 + x07) | 0;
        x13 = rotl$1(x13 ^ x02, 8);
        x08 = (x08 + x13) | 0;
        x07 = rotl$1(x07 ^ x08, 7);
        x03 = (x03 + x04) | 0;
        x14 = rotl$1(x14 ^ x03, 16);
        x09 = (x09 + x14) | 0;
        x04 = rotl$1(x04 ^ x09, 12);
        x03 = (x03 + x04) | 0;
        x14 = rotl$1(x14 ^ x03, 8);
        x09 = (x09 + x14) | 0;
        x04 = rotl$1(x04 ^ x09, 7);
    }
    // RFC 8439 §2.3 / §2.3.1: add the original state words back in state order.
    let oi = 0;
    out[oi++] = (y00 + x00) | 0;
    out[oi++] = (y01 + x01) | 0;
    out[oi++] = (y02 + x02) | 0;
    out[oi++] = (y03 + x03) | 0;
    out[oi++] = (y04 + x04) | 0;
    out[oi++] = (y05 + x05) | 0;
    out[oi++] = (y06 + x06) | 0;
    out[oi++] = (y07 + x07) | 0;
    out[oi++] = (y08 + x08) | 0;
    out[oi++] = (y09 + x09) | 0;
    out[oi++] = (y10 + x10) | 0;
    out[oi++] = (y11 + x11) | 0;
    out[oi++] = (y12 + x12) | 0;
    out[oi++] = (y13 + x13) | 0;
    out[oi++] = (y14 + x14) | 0;
    out[oi++] = (y15 + x15) | 0;
}
/**
 * hchacha hashes key and nonce into key' and nonce' for xchacha20.
 * Algorithmically identical to `hchacha_small`, but this exported path
 * normalizes word order on big-endian hosts.
 * Need to find a way to merge it with `chachaCore` without 25% performance hit.
 * @param s - Sigma constants as 32-bit words.
 * @param k - Key words.
 * @param i - Nonce-prefix words.
 * @param out - Output buffer for the derived subkey.
 * @example
 * Derives the XChaCha subkey from sigma, key, and nonce-prefix words.
 *
 * ```ts
 * const sigma = new Uint32Array(4);
 * const key = new Uint32Array(8);
 * const nonce = new Uint32Array(4);
 * const out = new Uint32Array(8);
 * hchacha(sigma, key, nonce, out);
 * ```
 */
// prettier-ignore
function hchacha(s, k, i, out) {
    let x00 = swap8IfBE(s[0]), x01 = swap8IfBE(s[1]), x02 = swap8IfBE(s[2]), x03 = swap8IfBE(s[3]), x04 = swap8IfBE(k[0]), x05 = swap8IfBE(k[1]), x06 = swap8IfBE(k[2]), x07 = swap8IfBE(k[3]), x08 = swap8IfBE(k[4]), x09 = swap8IfBE(k[5]), x10 = swap8IfBE(k[6]), x11 = swap8IfBE(k[7]), x12 = swap8IfBE(i[0]), x13 = swap8IfBE(i[1]), x14 = swap8IfBE(i[2]), x15 = swap8IfBE(i[3]);
    for (let r = 0; r < 20; r += 2) {
        x00 = (x00 + x04) | 0;
        x12 = rotl$1(x12 ^ x00, 16);
        x08 = (x08 + x12) | 0;
        x04 = rotl$1(x04 ^ x08, 12);
        x00 = (x00 + x04) | 0;
        x12 = rotl$1(x12 ^ x00, 8);
        x08 = (x08 + x12) | 0;
        x04 = rotl$1(x04 ^ x08, 7);
        x01 = (x01 + x05) | 0;
        x13 = rotl$1(x13 ^ x01, 16);
        x09 = (x09 + x13) | 0;
        x05 = rotl$1(x05 ^ x09, 12);
        x01 = (x01 + x05) | 0;
        x13 = rotl$1(x13 ^ x01, 8);
        x09 = (x09 + x13) | 0;
        x05 = rotl$1(x05 ^ x09, 7);
        x02 = (x02 + x06) | 0;
        x14 = rotl$1(x14 ^ x02, 16);
        x10 = (x10 + x14) | 0;
        x06 = rotl$1(x06 ^ x10, 12);
        x02 = (x02 + x06) | 0;
        x14 = rotl$1(x14 ^ x02, 8);
        x10 = (x10 + x14) | 0;
        x06 = rotl$1(x06 ^ x10, 7);
        x03 = (x03 + x07) | 0;
        x15 = rotl$1(x15 ^ x03, 16);
        x11 = (x11 + x15) | 0;
        x07 = rotl$1(x07 ^ x11, 12);
        x03 = (x03 + x07) | 0;
        x15 = rotl$1(x15 ^ x03, 8);
        x11 = (x11 + x15) | 0;
        x07 = rotl$1(x07 ^ x11, 7);
        x00 = (x00 + x05) | 0;
        x15 = rotl$1(x15 ^ x00, 16);
        x10 = (x10 + x15) | 0;
        x05 = rotl$1(x05 ^ x10, 12);
        x00 = (x00 + x05) | 0;
        x15 = rotl$1(x15 ^ x00, 8);
        x10 = (x10 + x15) | 0;
        x05 = rotl$1(x05 ^ x10, 7);
        x01 = (x01 + x06) | 0;
        x12 = rotl$1(x12 ^ x01, 16);
        x11 = (x11 + x12) | 0;
        x06 = rotl$1(x06 ^ x11, 12);
        x01 = (x01 + x06) | 0;
        x12 = rotl$1(x12 ^ x01, 8);
        x11 = (x11 + x12) | 0;
        x06 = rotl$1(x06 ^ x11, 7);
        x02 = (x02 + x07) | 0;
        x13 = rotl$1(x13 ^ x02, 16);
        x08 = (x08 + x13) | 0;
        x07 = rotl$1(x07 ^ x08, 12);
        x02 = (x02 + x07) | 0;
        x13 = rotl$1(x13 ^ x02, 8);
        x08 = (x08 + x13) | 0;
        x07 = rotl$1(x07 ^ x08, 7);
        x03 = (x03 + x04) | 0;
        x14 = rotl$1(x14 ^ x03, 16);
        x09 = (x09 + x14) | 0;
        x04 = rotl$1(x04 ^ x09, 12);
        x03 = (x03 + x04) | 0;
        x14 = rotl$1(x14 ^ x03, 8);
        x09 = (x09 + x14) | 0;
        x04 = rotl$1(x04 ^ x09, 7);
    }
    // HChaCha derives the subkey from state words 0..3 and 12..15 after 20 rounds.
    let oi = 0;
    out[oi++] = x00;
    out[oi++] = x01;
    out[oi++] = x02;
    out[oi++] = x03;
    out[oi++] = x12;
    out[oi++] = x13;
    out[oi++] = x14;
    out[oi++] = x15;
    swap32IfBE(out);
}
/**
 * ChaCha stream cipher. Conforms to RFC 8439 (IETF, TLS). 12-byte nonce, 4-byte counter.
 * With smaller nonce, it's not safe to make it random (CSPRNG), due to collision chance.
 * @param key - 32-byte key.
 * @param nonce - 12-byte nonce.
 * @param data - Input bytes to xor with the keystream.
 * @param output - Optional destination buffer.
 * @param counter - Initial block counter.
 * @returns Encrypted or decrypted bytes.
 * @example
 * Encrypts bytes with the RFC 8439 ChaCha20 stream cipher and a fresh key/nonce.
 *
 * ```ts
 * import { chacha20 } from '@noble/ciphers/chacha.js';
 * import { randomBytes } from '@noble/ciphers/utils.js';
 * const key = randomBytes(32);
 * const nonce = randomBytes(12);
 * chacha20(key, nonce, new Uint8Array(4));
 * ```
 */
const chacha20$1 = /* @__PURE__ */ createCipher(chachaCore, {
    counterRight: false,
    counterLength: 4,
    allowShortKeys: false,
});
/**
 * XChaCha eXtended-nonce ChaCha. With 24-byte nonce, it's safe to make it random (CSPRNG).
 * See {@link https://datatracker.ietf.org/doc/html/draft-irtf-cfrg-xchacha | the IRTF draft}.
 * The nonce/counter layout still reserves 8 counter bytes internally, but the shared public
 * `counter` argument follows noble's strict non-wrapping 32-bit policy. See `src/_arx.ts`
 * near `MAX_COUNTER` for the full counter-policy rationale.
 * @param key - 32-byte key.
 * @param nonce - 24-byte extended nonce.
 * @param data - Input bytes to xor with the keystream.
 * @param output - Optional destination buffer.
 * @param counter - Initial block counter.
 * @returns Encrypted or decrypted bytes.
 * @example
 * Encrypts bytes with XChaCha20 using a fresh key and random 24-byte nonce.
 *
 * ```ts
 * import { xchacha20 } from '@noble/ciphers/chacha.js';
 * import { randomBytes } from '@noble/ciphers/utils.js';
 * const key = randomBytes(32);
 * const nonce = randomBytes(24);
 * xchacha20(key, nonce, new Uint8Array(4));
 * ```
 */
const xchacha20$1 = /* @__PURE__ */ createCipher(chachaCore, {
    counterRight: false,
    counterLength: 8,
    extendNonceFn: hchacha,
    allowShortKeys: false,
});
// RFC 8439 §2.8.1 pad16(x): shared zero block for AAD/ciphertext padding.
const ZEROS16 = /* @__PURE__ */ new Uint8Array(16);
// RFC 8439 §2.8 / §2.8.1: aligned inputs add nothing, otherwise append 16-(len%16) zero bytes.
const updatePadded = (h, msg) => {
    h.update(msg);
    const leftover = msg.length % 16;
    if (leftover)
        h.update(ZEROS16.subarray(leftover));
};
// RFC 8439 §2.6.1 poly1305_key_gen returns `block[0..31]`, so AEAD key
// generation only needs 32 zero bytes.
const ZEROS32 = /* @__PURE__ */ new Uint8Array(32);
function computeTag(fn, key, nonce, ciphertext, AAD) {
    if (AAD !== undefined)
        abytes(AAD, undefined, 'AAD');
    // RFC 8439 §2.6 / §2.8: derive the Poly1305 one-time key from counter 0,
    // then MAC AAD || pad16(AAD) || ciphertext || pad16(ciphertext) || len(AAD) || len(ciphertext).
    const authKey = fn(key, nonce, ZEROS32);
    const lengths = u64Lengths(ciphertext.length, AAD ? AAD.length : 0, true);
    // Methods below can be replaced with
    // return poly1305_computeTag_small(authKey, lengths, ciphertext, AAD)
    const h = poly1305.create(authKey);
    if (AAD)
        updatePadded(h, AAD);
    updatePadded(h, ciphertext);
    h.update(lengths);
    const res = h.digest();
    clean(authKey, lengths);
    return res;
}
/**
 * AEAD algorithm from RFC 8439.
 * Salsa20 and chacha (RFC 8439) use poly1305 differently.
 * We could have composed them, but it's hard because of authKey:
 * In salsa20, authKey changes position in salsa stream.
 * In chacha, authKey can't be computed inside computeTag, it modifies the counter.
 */
const _poly1305_aead = (xorStream) => (key, nonce, AAD) => {
    // This borrows caller key/nonce/AAD buffers by reference; mutating them after construction
    // changes future encrypt/decrypt results.
    const tagLength = 16;
    return {
        encrypt(plaintext, output) {
            const plength = plaintext.length;
            output = getOutput(plength + tagLength, output, false);
            output.set(plaintext);
            const oPlain = output.subarray(0, -tagLength);
            // RFC 8439 §2.8: payload encryption starts at counter 1 because counter 0 produced the OTK.
            xorStream(key, nonce, oPlain, oPlain, 1);
            const tag = computeTag(xorStream, key, nonce, oPlain, AAD);
            output.set(tag, plength); // append tag
            clean(tag);
            return output;
        },
        decrypt(ciphertext, output) {
            output = getOutput(ciphertext.length - tagLength, output, false);
            const data = ciphertext.subarray(0, -tagLength);
            const passedTag = ciphertext.subarray(-tagLength);
            const tag = computeTag(xorStream, key, nonce, data, AAD);
            // RFC 8439 §2.8 / §4: authenticate ciphertext before decrypting it, and compare tags with
            // the constant-time equalBytes() helper rather than decrypting speculative plaintext first.
            if (!equalBytes(passedTag, tag)) {
                clean(tag);
                throw new Error('invalid tag');
            }
            output.set(ciphertext.subarray(0, -tagLength));
            // Actual decryption
            xorStream(key, nonce, output, output, 1); // start stream with i=1
            clean(tag);
            return output;
        },
    };
};
/**
 * ChaCha20-Poly1305 from RFC 8439.
 *
 * Unsafe to use random nonces under the same key, due to collision chance.
 * Prefer XChaCha instead.
 * @param key - 32-byte key.
 * @param nonce - 12-byte nonce.
 * @param AAD - Additional authenticated data.
 * @returns AEAD cipher instance.
 * @example
 * Encrypts and authenticates plaintext with a fresh key and nonce.
 *
 * ```ts
 * import { chacha20poly1305 } from '@noble/ciphers/chacha.js';
 * import { randomBytes } from '@noble/ciphers/utils.js';
 * const key = randomBytes(32);
 * const nonce = randomBytes(12);
 * const cipher = chacha20poly1305(key, nonce);
 * cipher.encrypt(new Uint8Array([1, 2, 3]));
 * ```
 */
const chacha20poly1305 = /* @__PURE__ */ wrapCipher({ blockSize: 64, nonceLength: 12, tagLength: 16 }, 
/* @__PURE__ */ _poly1305_aead(chacha20$1));
/**
 * XChaCha20-Poly1305 extended-nonce chacha.
 *
 * Can be safely used with random nonces (CSPRNG).
 * See {@link https://datatracker.ietf.org/doc/html/draft-irtf-cfrg-xchacha | the IRTF draft}.
 * @param key - 32-byte key.
 * @param nonce - 24-byte nonce.
 * @param AAD - Additional authenticated data.
 * @returns AEAD cipher instance.
 * @example
 * Encrypts and authenticates plaintext with a fresh key and random 24-byte nonce.
 *
 * ```ts
 * import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
 * import { randomBytes } from '@noble/ciphers/utils.js';
 * const key = randomBytes(32);
 * const nonce = randomBytes(24);
 * const cipher = xchacha20poly1305(key, nonce);
 * cipher.encrypt(new Uint8Array([1, 2, 3]));
 * ```
 */
const xchacha20poly1305 = /* @__PURE__ */ wrapCipher({ blockSize: 64, nonceLength: 24, tagLength: 16 }, 
/* @__PURE__ */ _poly1305_aead(xchacha20$1));

const KEY_BYTES = 32;
const CHACHA20_NONCE_BYTES = 12;
const XCHACHA20_NONCE_BYTES = 24;
const TAG_BYTES = 16;
const STREAM_BLOCK_BYTES = 64;
const MAX_COUNTER = 0xffffffff; // exclusive upper bound, matching @noble/ciphers
const chacha20 = {
    kind: 'cipher',
    name: 'ChaCha20',
    type: 'stream',
    keySizes: [KEY_BYTES],
    createEncryptor(params) {
        return createStreamTransform(params, 'ChaCha20');
    },
    createDecryptor(params) {
        return createStreamTransform(params, 'ChaCha20');
    },
};
const xchacha20 = {
    kind: 'cipher',
    name: 'XChaCha20',
    type: 'stream',
    keySizes: [KEY_BYTES],
    createEncryptor(params) {
        return createStreamTransform(params, 'XChaCha20');
    },
    createDecryptor(params) {
        return createStreamTransform(params, 'XChaCha20');
    },
};
const chacha20Poly1305 = {
    kind: 'aead',
    name: 'ChaCha20-Poly1305',
    keySizes: [KEY_BYTES],
    nonceSizes: [CHACHA20_NONCE_BYTES],
    recommendedNonceSize: CHACHA20_NONCE_BYTES,
    tagSizes: [TAG_BYTES],
    create({ key }) {
        return createAeadTransform(key, 'ChaCha20-Poly1305');
    },
};
const xchacha20Poly1305 = {
    kind: 'aead',
    name: 'XChaCha20-Poly1305',
    keySizes: [KEY_BYTES],
    nonceSizes: [XCHACHA20_NONCE_BYTES],
    recommendedNonceSize: XCHACHA20_NONCE_BYTES,
    tagSizes: [TAG_BYTES],
    create({ key }) {
        return createAeadTransform(key, 'XChaCha20-Poly1305');
    },
};
const allChaCha20Components = [
    chacha20,
    xchacha20,
    chacha20Poly1305,
    xchacha20Poly1305,
];
const chacha20Preset = {
    kind: 'preset',
    name: 'chacha20',
    components() {
        return allChaCha20Components;
    },
};
function createStreamTransform(params, algorithm) {
    assertUnsupportedBlockOptions(params.options, algorithm);
    assertBytes(params.key, `${algorithm} key`);
    assertKey$1(params.key, algorithm);
    const options = asRecord(params.options);
    const nonce = requireNonce(options, algorithm);
    let blockCounter = resolveCounter(options.counter, algorithm);
    let keystream = new Uint8Array(0);
    let keystreamOffset = 0;
    let finalized = false;
    const xorChunk = (input) => {
        if (input.length === 0) {
            return new Uint8Array(0);
        }
        const output = new Uint8Array(input.length);
        let inputOffset = 0;
        while (inputOffset < input.length) {
            if (keystreamOffset >= keystream.length) {
                const remaining = input.length - inputOffset;
                const generateLength = Math.ceil(remaining / STREAM_BLOCK_BYTES) * STREAM_BLOCK_BYTES;
                keystream = applyStream(algorithm, params.key, nonce, new Uint8Array(generateLength), blockCounter);
                blockCounter += generateLength / STREAM_BLOCK_BYTES;
                keystreamOffset = 0;
            }
            const n = Math.min(input.length - inputOffset, keystream.length - keystreamOffset);
            for (let i = 0; i < n; i++) {
                output[inputOffset + i] = input[inputOffset + i] ^ keystream[keystreamOffset + i];
            }
            inputOffset += n;
            keystreamOffset += n;
        }
        return output;
    };
    return {
        process(input) {
            assertNotFinalized$1(finalized);
            assertBytes(input, `${algorithm} input`);
            return xorChunk(input);
        },
        finalize(input = new Uint8Array(0)) {
            assertNotFinalized$1(finalized);
            assertBytes(input, `${algorithm} input`);
            finalized = true;
            const output = input.length === 0 ? new Uint8Array(0) : xorChunk(input);
            keystream = new Uint8Array(0);
            keystreamOffset = 0;
            return output;
        },
    };
}
function createAeadTransform(key, algorithm) {
    assertBytes(key, `${algorithm} key`);
    assertKey$1(key, algorithm);
    return {
        createSealer({ nonce, aad, tagLength }) {
            const resolvedNonce = requireAeadNonce(nonce, algorithm);
            const resolvedAad = resolveAad(aad, algorithm);
            assertTagLength(tagLength, algorithm);
            let pendings = [];
            let finalized = false;
            return {
                process(input) {
                    assertNotFinalized$1(finalized);
                    assertBytes(input, `${algorithm} input`);
                    pendings.push(input);
                    return new Uint8Array(0);
                },
                finalize(input = new Uint8Array(0)) {
                    assertNotFinalized$1(finalized);
                    if (input.length !== 0) {
                        this.process(input);
                    }
                    finalized = true;
                    const plaintext = collectPending(pendings);
                    pendings = [];
                    const cipher = createNobleAead(algorithm, key, resolvedNonce, resolvedAad);
                    return cipher.encrypt(plaintext);
                },
            };
        },
        createOpener({ nonce, aad, tag, tagLength }) {
            const resolvedNonce = requireAeadNonce(nonce, algorithm);
            const resolvedAad = resolveAad(aad, algorithm);
            const detachedTag = resolveOptionalTag(tag, algorithm);
            // When a detached tag is present, tag.length wins and tagLength is ignored.
            if (detachedTag === undefined) {
                assertTagLength(tagLength, algorithm);
            }
            let pendings = [];
            let finalized = false;
            return {
                process(input) {
                    assertNotFinalized$1(finalized);
                    assertBytes(input, `${algorithm} input`);
                    pendings.push(input);
                    return new Uint8Array(0);
                },
                finalize(input = new Uint8Array(0)) {
                    assertNotFinalized$1(finalized);
                    if (input.length !== 0) {
                        this.process(input);
                    }
                    finalized = true;
                    const ciphertext = collectPending(pendings);
                    pendings = [];
                    const sealed = resolveSealedInput(ciphertext, detachedTag, algorithm);
                    const cipher = createNobleAead(algorithm, key, resolvedNonce, resolvedAad);
                    try {
                        return cipher.decrypt(sealed);
                    }
                    catch {
                        throw new Error(`${algorithm} authentication failed.`);
                    }
                },
            };
        },
    };
}
function collectPending(pendings) {
    if (pendings.length === 0) {
        return new Uint8Array(0);
    }
    if (pendings.length === 1) {
        return pendings[0];
    }
    return concatBytes(...pendings);
}
function applyStream(algorithm, key, nonce, data, counter) {
    if (algorithm === 'ChaCha20') {
        return chacha20$1(key, nonce, data, undefined, counter);
    }
    return xchacha20$1(key, nonce, data, undefined, counter);
}
function createNobleAead(algorithm, key, nonce, aad) {
    if (algorithm === 'ChaCha20-Poly1305') {
        return chacha20poly1305(key, nonce, aad);
    }
    return xchacha20poly1305(key, nonce, aad);
}
function resolveSealedInput(ciphertext, tag, algorithm) {
    if (tag === undefined) {
        if (ciphertext.length < TAG_BYTES) {
            throw new Error(`${algorithm} ciphertext must include a 128-bit authentication tag.`);
        }
        return ciphertext;
    }
    assertTag(tag, algorithm);
    return concatBytes(ciphertext, tag);
}
function requireNonce(options, algorithm) {
    const nonce = options.nonce;
    if (nonce === undefined) {
        throw new Error(`${algorithm} requires a nonce.`);
    }
    assertBytes(nonce, `${algorithm} nonce`);
    assertNonce(nonce, algorithm);
    return nonce;
}
function requireAeadNonce(nonce, algorithm) {
    if (nonce === undefined) {
        throw new Error(`${algorithm} requires a nonce.`);
    }
    assertBytes(nonce, `${algorithm} nonce`);
    assertNonce(nonce, algorithm);
    return nonce;
}
function resolveOptionalTag(tag, algorithm) {
    if (tag === undefined) {
        return undefined;
    }
    assertBytes(tag, `${algorithm} tag`);
    assertTag(tag, algorithm);
    return tag;
}
function resolveAad(aad, algorithm) {
    if (aad === undefined) {
        return new Uint8Array(0);
    }
    assertBytes(aad, `${algorithm} aad`);
    return aad;
}
function assertTagLength(tagLength, algorithm) {
    if (tagLength !== undefined && tagLength !== TAG_BYTES) {
        throw new Error(`${algorithm} tagLength must be 16 bytes.`);
    }
}
function resolveCounter(counter, algorithm) {
    if (counter === undefined) {
        return 0;
    }
    if (typeof counter !== 'number' || !Number.isInteger(counter) || counter < 0 || counter >= MAX_COUNTER) {
        throw new RangeError(`${algorithm} counter must be a 32-bit unsigned integer.`);
    }
    return counter;
}
function assertKey$1(key, algorithm) {
    if (key.length !== KEY_BYTES) {
        throw new Error(`${algorithm} key must be 256 bits.`);
    }
}
function assertNonce(nonce, algorithm) {
    if (algorithm === 'ChaCha20' || algorithm === 'ChaCha20-Poly1305') {
        if (nonce.length !== CHACHA20_NONCE_BYTES) {
            throw new Error(`${algorithm} nonce must be 96 bits.`);
        }
        return;
    }
    if (nonce.length !== XCHACHA20_NONCE_BYTES) {
        throw new Error(`${algorithm} nonce must be 192 bits.`);
    }
}
function assertTag(tag, algorithm) {
    if (tag.length !== TAG_BYTES) {
        throw new Error(`${algorithm} tag must be 128 bits.`);
    }
}
function assertUnsupportedBlockOptions(options, algorithm) {
    const record = asRecord(options);
    if (Object.prototype.hasOwnProperty.call(record, 'mode') && record.mode !== undefined) {
        throw new Error(`${algorithm} does not support mode.`);
    }
    if (Object.prototype.hasOwnProperty.call(record, 'padding') && record.padding !== undefined) {
        throw new Error(`${algorithm} does not support padding.`);
    }
}
function asRecord(options) {
    if (typeof options === 'object' && options !== null) {
        return options;
    }
    return {};
}
function assertNotFinalized$1(finalized) {
    if (finalized) {
        throw new Error('Transform is already finalized.');
    }
}

const des = {
    kind: 'cipher',
    name: 'DES',
    type: 'block',
    blockSize: 8,
    keySizes: [8],
    create(key) {
        return createDesCipher(key);
    },
};
const desPreset = {
    kind: 'preset',
    name: 'des',
    components() {
        return [des];
    },
};
function createDesCipher(key) {
    if (key.length !== 8) {
        throw new Error('DES key must be 64 bits.');
    }
    const subkeys = createSubkeys(key);
    const tmp = [0, 0];
    return {
        blockSize: 8,
        encryptBlock(input, inputOffset, output, outputOffset) {
            processBlock(input, inputOffset, output, outputOffset, subkeys, tmp, true);
        },
        decryptBlock(input, inputOffset, output, outputOffset) {
            processBlock(input, inputOffset, output, outputOffset, subkeys, tmp, false);
        },
        encrypt(input, output) {
            return processBlocks(input, output, subkeys, true, 'DES');
        },
        decrypt(input, output) {
            return processBlocks(input, output, subkeys, false, 'DES');
        },
    };
}
function processBlocks(input, output, subkeys, encrypting, name) {
    assertBlocks$2(input, output, name);
    const tmp = [0, 0];
    for (let offset = 0; offset < input.length; offset += 8) {
        processBlock(input, offset, output, offset, subkeys, tmp, encrypting);
    }
    return output;
}
function createSubkeys(key) {
    const subkeys = new Array(32);
    let keyLeft = readUint32BE(key, 0);
    let keyRight = readUint32BE(key, 4);
    const tmp = [0, 0];
    permutedChoice1(keyLeft, keyRight, tmp, 0);
    keyLeft = tmp[0];
    keyRight = tmp[1];
    for (let index = 0; index < subkeys.length; index += 2) {
        const shift = KEY_SHIFTS[index >>> 1];
        keyLeft = rotateLeft28(keyLeft, shift);
        keyRight = rotateLeft28(keyRight, shift);
        permutedChoice2(keyLeft, keyRight, subkeys, index);
    }
    return subkeys;
}
function processBlock(input, inputOffset, output, outputOffset, subkeys, tmp, encrypting) {
    let left = readUint32BE(input, inputOffset);
    let right = readUint32BE(input, inputOffset + 4);
    initialPermutation(left, right, tmp, 0);
    left = tmp[0];
    right = tmp[1];
    if (encrypting) {
        for (let index = 0; index < subkeys.length; index += 2) {
            const nextLeft = right;
            right = (left ^ feistel(right, subkeys[index], subkeys[index + 1], tmp)) >>> 0;
            left = nextLeft;
        }
        reverseInitialPermutation(right, left, tmp, 0);
    }
    else {
        const originalLeft = left;
        left = right;
        right = originalLeft;
        for (let index = subkeys.length - 2; index >= 0; index -= 2) {
            const nextLeft = left;
            left = (right ^ feistel(left, subkeys[index], subkeys[index + 1], tmp)) >>> 0;
            right = nextLeft;
        }
        reverseInitialPermutation(left, right, tmp, 0);
    }
    writeUint32BE(output, tmp[0], outputOffset);
    writeUint32BE(output, tmp[1], outputOffset + 4);
}
function feistel(right, keyLeft, keyRight, tmp) {
    expand(right, tmp, 0);
    return permute(substitute((keyLeft ^ tmp[0]) >>> 0, (keyRight ^ tmp[1]) >>> 0));
}
function readUint32BE(bytes, offset) {
    return ((bytes[offset] << 24)
        | (bytes[offset + 1] << 16)
        | (bytes[offset + 2] << 8)
        | bytes[offset + 3]) >>> 0;
}
function writeUint32BE(bytes, value, offset) {
    bytes[offset] = value >>> 24;
    bytes[offset + 1] = value >>> 16;
    bytes[offset + 2] = value >>> 8;
    bytes[offset + 3] = value;
}
// Word-based DES helpers adapted from des.js (MIT, Fedor Indutny).
function initialPermutation(inputLeft, inputRight, output, offset) {
    let outputLeft = 0;
    let outputRight = 0;
    for (let bit = 6; bit >= 0; bit -= 2) {
        for (let shift = 0; shift <= 24; shift += 8) {
            outputLeft = (outputLeft << 1) | ((inputRight >>> (shift + bit)) & 1);
        }
        for (let shift = 0; shift <= 24; shift += 8) {
            outputLeft = (outputLeft << 1) | ((inputLeft >>> (shift + bit)) & 1);
        }
    }
    for (let bit = 6; bit >= 0; bit -= 2) {
        for (let shift = 1; shift <= 25; shift += 8) {
            outputRight = (outputRight << 1) | ((inputRight >>> (shift + bit)) & 1);
        }
        for (let shift = 1; shift <= 25; shift += 8) {
            outputRight = (outputRight << 1) | ((inputLeft >>> (shift + bit)) & 1);
        }
    }
    output[offset] = outputLeft >>> 0;
    output[offset + 1] = outputRight >>> 0;
}
function reverseInitialPermutation(inputLeft, inputRight, output, offset) {
    let outputLeft = 0;
    let outputRight = 0;
    for (let bit = 0; bit < 4; bit++) {
        for (let shift = 24; shift >= 0; shift -= 8) {
            outputLeft = (outputLeft << 1) | ((inputRight >>> (shift + bit)) & 1);
            outputLeft = (outputLeft << 1) | ((inputLeft >>> (shift + bit)) & 1);
        }
    }
    for (let bit = 4; bit < 8; bit++) {
        for (let shift = 24; shift >= 0; shift -= 8) {
            outputRight = (outputRight << 1) | ((inputRight >>> (shift + bit)) & 1);
            outputRight = (outputRight << 1) | ((inputLeft >>> (shift + bit)) & 1);
        }
    }
    output[offset] = outputLeft >>> 0;
    output[offset + 1] = outputRight >>> 0;
}
function permutedChoice1(inputLeft, inputRight, output, offset) {
    let outputLeft = 0;
    let outputRight = 0;
    let bit = 0;
    for (bit = 7; bit >= 5; bit--) {
        for (let shift = 0; shift <= 24; shift += 8) {
            outputLeft = (outputLeft << 1) | ((inputRight >> (shift + bit)) & 1);
        }
        for (let shift = 0; shift <= 24; shift += 8) {
            outputLeft = (outputLeft << 1) | ((inputLeft >> (shift + bit)) & 1);
        }
    }
    for (let shift = 0; shift <= 24; shift += 8) {
        outputLeft = (outputLeft << 1) | ((inputRight >> (shift + bit)) & 1);
    }
    for (bit = 1; bit <= 3; bit++) {
        for (let shift = 0; shift <= 24; shift += 8) {
            outputRight = (outputRight << 1) | ((inputRight >> (shift + bit)) & 1);
        }
        for (let shift = 0; shift <= 24; shift += 8) {
            outputRight = (outputRight << 1) | ((inputLeft >> (shift + bit)) & 1);
        }
    }
    for (let shift = 0; shift <= 24; shift += 8) {
        outputRight = (outputRight << 1) | ((inputLeft >> (shift + bit)) & 1);
    }
    output[offset] = outputLeft >>> 0;
    output[offset + 1] = outputRight >>> 0;
}
function rotateLeft28(value, shift) {
    return ((value << shift) & 0xfffffff) | (value >>> (28 - shift));
}
const PERMUTED_CHOICE2_TABLE = [
    14, 11, 17, 4, 27, 23, 25, 0,
    13, 22, 7, 18, 5, 9, 16, 24,
    2, 20, 12, 21, 1, 8, 15, 26,
    15, 4, 25, 19, 9, 1, 26, 16,
    5, 11, 23, 8, 12, 7, 17, 0,
    22, 3, 10, 14, 6, 20, 27, 24,
];
function permutedChoice2(inputLeft, inputRight, output, offset) {
    let outputLeft = 0;
    let outputRight = 0;
    const halfLength = PERMUTED_CHOICE2_TABLE.length >>> 1;
    for (let index = 0; index < halfLength; index++) {
        outputLeft = (outputLeft << 1) | ((inputLeft >>> PERMUTED_CHOICE2_TABLE[index]) & 1);
    }
    for (let index = halfLength; index < PERMUTED_CHOICE2_TABLE.length; index++) {
        outputRight = (outputRight << 1) | ((inputRight >>> PERMUTED_CHOICE2_TABLE[index]) & 1);
    }
    output[offset] = outputLeft >>> 0;
    output[offset + 1] = outputRight >>> 0;
}
function expand(right, output, offset) {
    let outputLeft = ((right & 1) << 5) | (right >>> 27);
    let outputRight = 0;
    for (let bit = 23; bit >= 15; bit -= 4) {
        outputLeft = (outputLeft << 6) | ((right >>> bit) & 0x3f);
    }
    for (let bit = 11; bit >= 3; bit -= 4) {
        outputRight |= (right >>> bit) & 0x3f;
        outputRight <<= 6;
    }
    outputRight |= ((right & 0x1f) << 1) | (right >>> 31);
    output[offset] = outputLeft >>> 0;
    output[offset + 1] = outputRight >>> 0;
}
function substitute(inputLeft, inputRight) {
    let output = 0;
    for (let index = 0; index < 4; index++) {
        output = (output << 4) | S_TABLE[index * 0x40 + ((inputLeft >>> (18 - index * 6)) & 0x3f)];
    }
    for (let index = 0; index < 4; index++) {
        output = (output << 4) | S_TABLE[4 * 0x40 + index * 0x40 + ((inputRight >>> (18 - index * 6)) & 0x3f)];
    }
    return output >>> 0;
}
const PERMUTE_TABLE = [
    16, 25, 12, 11, 3, 20, 4, 15, 31, 17, 9, 6, 27, 14, 1, 22,
    30, 24, 8, 18, 0, 5, 29, 23, 13, 19, 2, 26, 10, 21, 28, 7,
];
function permute(value) {
    let output = 0;
    for (const bit of PERMUTE_TABLE) {
        output = (output << 1) | ((value >>> bit) & 1);
    }
    return output >>> 0;
}
const KEY_SHIFTS = [
    1, 1, 2, 2, 2, 2, 2, 2,
    1, 2, 2, 2, 2, 2, 2, 1,
];
const S_TABLE = [
    14, 0, 4, 15, 13, 7, 1, 4, 2, 14, 15, 2, 11, 13, 8, 1,
    3, 10, 10, 6, 6, 12, 12, 11, 5, 9, 9, 5, 0, 3, 7, 8,
    4, 15, 1, 12, 14, 8, 8, 2, 13, 4, 6, 9, 2, 1, 11, 7,
    15, 5, 12, 11, 9, 3, 7, 14, 3, 10, 10, 0, 5, 6, 0, 13,
    15, 3, 1, 13, 8, 4, 14, 7, 6, 15, 11, 2, 3, 8, 4, 14,
    9, 12, 7, 0, 2, 1, 13, 10, 12, 6, 0, 9, 5, 11, 10, 5,
    0, 13, 14, 8, 7, 10, 11, 1, 10, 3, 4, 15, 13, 4, 1, 2,
    5, 11, 8, 6, 12, 7, 6, 12, 9, 0, 3, 5, 2, 14, 15, 9,
    10, 13, 0, 7, 9, 0, 14, 9, 6, 3, 3, 4, 15, 6, 5, 10,
    1, 2, 13, 8, 12, 5, 7, 14, 11, 12, 4, 11, 2, 15, 8, 1,
    13, 1, 6, 10, 4, 13, 9, 0, 8, 6, 15, 9, 3, 8, 0, 7,
    11, 4, 1, 15, 2, 14, 12, 3, 5, 11, 10, 5, 14, 2, 7, 12,
    7, 13, 13, 8, 14, 11, 3, 5, 0, 6, 6, 15, 9, 0, 10, 3,
    1, 4, 2, 7, 8, 2, 5, 12, 11, 1, 12, 10, 4, 14, 15, 9,
    10, 3, 6, 15, 9, 0, 0, 6, 12, 10, 11, 1, 7, 13, 13, 8,
    15, 9, 1, 4, 3, 5, 14, 11, 5, 12, 2, 7, 8, 2, 4, 14,
    2, 14, 12, 11, 4, 2, 1, 12, 7, 4, 10, 7, 11, 13, 6, 1,
    8, 5, 5, 0, 3, 15, 15, 10, 13, 3, 0, 9, 14, 8, 9, 6,
    4, 11, 2, 8, 1, 12, 11, 7, 10, 1, 13, 14, 7, 2, 8, 13,
    15, 6, 9, 15, 12, 0, 5, 9, 6, 10, 3, 4, 0, 5, 14, 3,
    12, 10, 1, 15, 10, 4, 15, 2, 9, 7, 2, 12, 6, 9, 8, 5,
    0, 6, 13, 1, 3, 13, 4, 14, 14, 0, 7, 11, 5, 3, 11, 8,
    9, 4, 14, 3, 15, 2, 5, 12, 2, 9, 8, 5, 12, 15, 3, 10,
    7, 11, 0, 14, 4, 1, 10, 7, 1, 6, 13, 0, 11, 8, 6, 13,
    4, 13, 11, 0, 2, 11, 14, 7, 15, 4, 0, 9, 8, 1, 13, 10,
    3, 14, 12, 3, 9, 5, 7, 12, 5, 2, 10, 15, 6, 8, 1, 6,
    1, 6, 4, 11, 11, 13, 13, 8, 12, 1, 3, 4, 7, 10, 14, 7,
    10, 9, 15, 5, 6, 0, 8, 15, 0, 14, 5, 2, 9, 3, 2, 12,
    13, 1, 2, 15, 8, 13, 4, 8, 6, 10, 15, 3, 11, 7, 1, 4,
    10, 12, 9, 5, 3, 6, 14, 11, 5, 0, 0, 14, 12, 9, 7, 2,
    7, 2, 11, 1, 4, 14, 1, 7, 9, 4, 12, 10, 14, 8, 2, 13,
    0, 15, 6, 12, 10, 9, 13, 0, 15, 3, 3, 5, 5, 6, 8, 11,
];
function assertBlocks$2(input, output, name) {
    if (input.length % 8 !== 0) {
        throw new Error(`${name} input length must be a multiple of 64 bits.`);
    }
    if (output.length !== input.length) {
        throw new Error(`${name} output length must equal input length.`);
    }
}

const rc4 = {
    kind: 'cipher',
    name: 'RC4',
    type: 'stream',
    createEncryptor({ key, options }) {
        return createRc4Transform(key, getDrop(options, 0));
    },
    createDecryptor({ key, options }) {
        return createRc4Transform(key, getDrop(options, 0));
    },
};
const rc4Drop = {
    kind: 'cipher',
    name: 'RC4Drop',
    type: 'stream',
    createEncryptor({ key, options }) {
        return createRc4Transform(key, getDrop(options, 192));
    },
    createDecryptor({ key, options }) {
        return createRc4Transform(key, getDrop(options, 192));
    },
};
const rc4Preset = {
    kind: 'preset',
    name: 'rc4',
    components() {
        return [rc4, rc4Drop];
    },
};
function createRc4Transform(key, drop = 0) {
    assertKey(key);
    assertDrop(drop);
    const state = createInitialState(key);
    for (let n = 0; n < drop; n++) {
        nextByte(state);
    }
    let finalized = false;
    const processInput = (input) => {
        const output = new Uint8Array(input.length);
        for (let i = 0; i < input.length; i++) {
            output[i] = input[i] ^ nextByte(state);
        }
        return output;
    };
    return {
        process(input) {
            assertNotFinalized(finalized);
            return processInput(input);
        },
        finalize(input = new Uint8Array(0)) {
            assertNotFinalized(finalized);
            finalized = true;
            return input.length === 0 ? new Uint8Array(0) : processInput(input);
        },
    };
}
function createInitialState(key) {
    const s = new Uint8Array(256);
    for (let i = 0; i < s.length; i++) {
        s[i] = i;
    }
    let j = 0;
    for (let i = 0; i < s.length; i++) {
        j = (j + s[i] + key[i % key.length]) & 0xff;
        swap(s, i, j);
    }
    return { s, i: 0, j: 0 };
}
function nextByte(state) {
    const { s } = state;
    state.i = (state.i + 1) & 0xff;
    state.j = (state.j + s[state.i]) & 0xff;
    swap(s, state.i, state.j);
    return s[(s[state.i] + s[state.j]) & 0xff];
}
function swap(bytes, a, b) {
    const value = bytes[a];
    bytes[a] = bytes[b];
    bytes[b] = value;
}
function getDrop(options, defaultDrop) {
    if (!isRc4Options(options) || options.drop === undefined) {
        return defaultDrop;
    }
    return options.drop;
}
function isRc4Options(options) {
    return typeof options === 'object' && options !== null;
}
function assertKey(key) {
    if (key.length < 5) {
        throw new Error('RC4 key should be greater or equal than 40 bits.');
    }
}
function assertDrop(drop) {
    if (!Number.isInteger(drop) || drop < 0) {
        throw new RangeError('RC4 drop must be a non-negative integer.');
    }
}
function assertNotFinalized(finalized) {
    if (finalized) {
        throw new Error('Transform is already finalized.');
    }
}

/*!
 * js-speck v0.1.0
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */

// src/bytes.ts
var HAS_TYPED_ARRAY = typeof ArrayBuffer !== "undefined" && typeof Uint8Array !== "undefined" && typeof Uint32Array !== "undefined";
var ByteArrayClass = HAS_TYPED_ARRAY ? Uint8Array : Array;
var WordArrayClass = HAS_TYPED_ARRAY ? Uint32Array : Array;
var INPUT_ERROR = "input is invalid type";
var isArray = !Array.isArray ? function(obj) {
  return Object.prototype.toString.call(obj) === "[object Array]";
} : Array.isArray;
function toByteArray(input) {
  const type = typeof input;
  if (type !== "object" || input === null) {
    throw new Error(INPUT_ERROR);
  }
  if (HAS_TYPED_ARRAY) {
    if (input instanceof Uint8Array) {
      return input;
    }
    if (input instanceof ArrayBuffer) {
      return new Uint8Array(input);
    }
  }
  if (!isArray(input)) {
    throw new Error(INPUT_ERROR);
  }
  return input;
}
function assertByteLength(input, length, name) {
  if (input.length !== length) {
    throw new RangeError(`${name} must be ${length} bytes.`);
  }
}
function toWordLE(bytes, wordBytes) {
  var words = new WordArrayClass(bytes.length / wordBytes);
  for (var i = 0; i < words.length; ++i) {
    var offset = i * wordBytes;
    var value = 0;
    for (var j = 0; j < wordBytes; ++j) {
      value += bytes[offset + j] * Math.pow(2, j * 8);
    }
    words[i] = value >>> 0;
  }
  return words;
}
function fromWordLE(words, wordBytes) {
  var bytes = new ByteArrayClass(words.length * wordBytes);
  for (var i = 0; i < words.length; ++i) {
    var offset = i * wordBytes;
    var value = words[i];
    for (var j = 0; j < wordBytes; ++j) {
      bytes[offset + j] = Math.floor(value / Math.pow(2, j * 8)) & 255;
    }
  }
  return bytes;
}

// src/speck.ts
function createMask(wordBits) {
  return wordBits === 32 ? 4294967295 : (1 << wordBits) - 1;
}
function normalize(value, mask, wordBits) {
  return wordBits === 32 ? value >>> 0 : value & mask;
}
function rotr(value, shift, wordBits, mask) {
  return normalize(value >>> shift | value << wordBits - shift, mask, wordBits);
}
function rotl(value, shift, wordBits, mask) {
  return normalize(value << shift | value >>> wordBits - shift, mask, wordBits);
}
function createSpeckVariant(config) {
  const wordBytes = config.wordBits / 8;
  const blockBytes = wordBytes * 2;
  const keyByteLength = wordBytes * config.keyWords;
  const mask = createMask(config.wordBits);
  function round(x, y, key) {
    x = normalize(rotr(x, config.alpha, config.wordBits, mask) + y, mask, config.wordBits);
    x = normalize(x ^ key, mask, config.wordBits);
    y = normalize(rotl(y, config.beta, config.wordBits, mask) ^ x, mask, config.wordBits);
    return [x, y];
  }
  function inverseRound(x, y, key) {
    y = rotr(normalize(y ^ x, mask, config.wordBits), config.beta, config.wordBits, mask);
    x = rotl(normalize((x ^ key) - y, mask, config.wordBits), config.alpha, config.wordBits, mask);
    return [x, y];
  }
  function assertBlockMultiple(bytes, name) {
    if (bytes.length % blockBytes !== 0) {
      throw new RangeError(`${name} length must be a multiple of ${blockBytes} bytes.`);
    }
  }
  function assertWordLength(words, length, name) {
    if (words.length !== length) {
      throw new RangeError(`${name} must contain ${length} words.`);
    }
  }
  function assertWordBlockMultiple(words, name) {
    if (words.length % 2 !== 0) {
      throw new RangeError(`${name} length must be a multiple of 2 words.`);
    }
  }
  function assertRoundKeysAndDataWords(roundKeys, dataWords) {
    if (roundKeys.length !== config.rounds) {
      throw new RangeError(`Round keys must contain ${config.rounds} words.`);
    }
    assertWordBlockMultiple(dataWords, "Data");
  }
  function toWords(key, data) {
    const keyBytes = toByteArray(key);
    assertByteLength(keyBytes, keyByteLength, "Key");
    const dataBytes = toByteArray(data);
    assertBlockMultiple(dataBytes, "Data");
    return [toWordLE(keyBytes, wordBytes), toWordLE(dataBytes, wordBytes)];
  }
  function expandKey11(keyWords) {
    assertWordLength(keyWords, config.keyWords, "Key");
    const l = new WordArrayClass(config.rounds + config.keyWords - 2);
    const roundKeys = new WordArrayClass(config.rounds);
    roundKeys[0] = keyWords[0];
    for (let i = 1; i < config.keyWords; ++i) {
      l[i - 1] = keyWords[i];
    }
    for (let i = 0; i < config.rounds - 1; ++i) {
      const result = round(l[i], roundKeys[i], i);
      l[i + config.keyWords - 1] = result[0];
      roundKeys[i + 1] = result[1];
    }
    return roundKeys;
  }
  function encrypt11(key, data) {
    const [keyWords, dataWords] = toWords(key, data);
    return fromWordLE(encryptWords11(keyWords, dataWords), wordBytes);
  }
  function encryptWords11(keyWords, dataWords) {
    return encryptWordsWithRoundKey11(expandKey11(keyWords), dataWords);
  }
  function encryptWordsWithRoundKey11(roundKeys, dataWords) {
    assertRoundKeysAndDataWords(roundKeys, dataWords);
    const result = new WordArrayClass(dataWords.length);
    for (let i = 0; i < dataWords.length; i += 2) {
      let y = dataWords[i];
      let x = dataWords[i + 1];
      for (let j = 0; j < config.rounds; ++j) {
        const r = round(x, y, roundKeys[j]);
        x = r[0];
        y = r[1];
      }
      result[i] = y;
      result[i + 1] = x;
    }
    return result;
  }
  function decrypt11(key, data) {
    const [keyWords, dataWords] = toWords(key, data);
    return fromWordLE(decryptWords11(keyWords, dataWords), wordBytes);
  }
  function decryptWords11(keyWords, dataWords) {
    return decryptWordsWithRoundKey11(expandKey11(keyWords), dataWords);
  }
  function decryptWordsWithRoundKey11(roundKeys, dataWords) {
    assertRoundKeysAndDataWords(roundKeys, dataWords);
    const result = new WordArrayClass(dataWords.length);
    for (let i = 0; i < dataWords.length; i += 2) {
      let y = dataWords[i];
      let x = dataWords[i + 1];
      for (let j = config.rounds - 1; j >= 0; --j) {
        const r = inverseRound(x, y, roundKeys[j]);
        x = r[0];
        y = r[1];
      }
      result[i] = y;
      result[i + 1] = x;
    }
    return result;
  }
  return {
    blockBytes,
    keyBytes: keyByteLength,
    rounds: config.rounds,
    expandKey: expandKey11,
    encrypt: encrypt11,
    decrypt: decrypt11,
    encryptWords: encryptWords11,
    decryptWords: decryptWords11,
    encryptWordsWithRoundKey: encryptWordsWithRoundKey11,
    decryptWordsWithRoundKey: decryptWordsWithRoundKey11
  };
}

// src/speck32-64.ts
var speck32_64Block = createSpeckVariant({
  wordBits: 16,
  keyWords: 4,
  rounds: 22,
  alpha: 7,
  beta: 2
});
var {
  expandKey,
  encrypt,
  decrypt,
  encryptWords,
  decryptWords,
  encryptWordsWithRoundKey,
  decryptWordsWithRoundKey
} = speck32_64Block;

// src/speck48-72.ts
var speck48_72Block = createSpeckVariant({
  wordBits: 24,
  keyWords: 3,
  rounds: 22,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey2,
  encrypt: encrypt2,
  decrypt: decrypt2,
  encryptWords: encryptWords2,
  decryptWords: decryptWords2,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey2,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey2
} = speck48_72Block;

// src/speck48-96.ts
var speck48_96Block = createSpeckVariant({
  wordBits: 24,
  keyWords: 4,
  rounds: 23,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey3,
  encrypt: encrypt3,
  decrypt: decrypt3,
  encryptWords: encryptWords3,
  decryptWords: decryptWords3,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey3,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey3
} = speck48_96Block;

// src/speck64-96.ts
var speck64_96Block = createSpeckVariant({
  wordBits: 32,
  keyWords: 3,
  rounds: 26,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey4,
  encrypt: encrypt4,
  decrypt: decrypt4,
  encryptWords: encryptWords4,
  decryptWords: decryptWords4,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey4,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey4
} = speck64_96Block;

// src/speck64-128.ts
var speck64_128Block = createSpeckVariant({
  wordBits: 32,
  keyWords: 4,
  rounds: 27,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey5,
  encrypt: encrypt5,
  decrypt: decrypt5,
  encryptWords: encryptWords5,
  decryptWords: decryptWords5,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey5,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey5
} = speck64_128Block;

// src/speck-long.ts
function addLong(aLo, aHi, bLo, bHi, highMask) {
  const lo = aLo + bLo >>> 0;
  const hi = (aHi + bHi + (lo < aLo >>> 0 ? 1 : 0) & highMask) >>> 0;
  return [lo, hi];
}
function subLong(aLo, aHi, bLo, bHi, highMask) {
  const lo = aLo - bLo >>> 0;
  const hi = (aHi - bHi - (aLo >>> 0 < bLo >>> 0 ? 1 : 0) & highMask) >>> 0;
  return [lo, hi];
}
function rotlLong(lo, hi, shift, highBits, highMask) {
  return [
    (lo << shift | hi >>> highBits - shift) >>> 0,
    ((hi << shift | lo >>> 32 - shift) & highMask) >>> 0
  ];
}
function rotrLong(lo, hi, shift, highBits, highMask) {
  const shiftMask = (1 << shift) - 1;
  return [
    (lo >>> shift | (hi & shiftMask) << 32 - shift) >>> 0,
    ((hi >>> shift | (lo & shiftMask) << highBits - shift) & highMask) >>> 0
  ];
}
function toLongWordPairsLE(bytes, wordBytes) {
  const lanesPerWord = 2;
  const words = new WordArrayClass(bytes.length / wordBytes * lanesPerWord);
  for (let i = 0; i < words.length; i += lanesPerWord) {
    const offset = i / lanesPerWord * wordBytes;
    words[i] = (bytes[offset] | bytes[offset + 1] << 8 | bytes[offset + 2] << 16 | bytes[offset + 3] << 24) >>> 0;
    let high = 0;
    for (let j = 4; j < wordBytes; ++j) {
      high |= bytes[offset + j] << (j - 4) * 8;
    }
    words[i + 1] = high >>> 0;
  }
  return words;
}
function fromLongWordPairsLE(words, wordBytes) {
  const lanesPerWord = 2;
  const bytes = new ByteArrayClass(words.length / lanesPerWord * wordBytes);
  for (let i = 0; i < words.length; i += lanesPerWord) {
    const lo = words[i];
    const hi = words[i + 1];
    const offset = i / lanesPerWord * wordBytes;
    bytes[offset] = lo & 255;
    bytes[offset + 1] = lo >>> 8 & 255;
    bytes[offset + 2] = lo >>> 16 & 255;
    bytes[offset + 3] = lo >>> 24 & 255;
    for (let j = 4; j < wordBytes; ++j) {
      bytes[offset + j] = hi >>> (j - 4) * 8 & 255;
    }
  }
  return bytes;
}
function createSpeckLongVariant(config) {
  const lanesPerWord = 2;
  const wordBytes = config.wordBits / 8;
  const highBits = config.wordBits - 32;
  const highMask = config.wordBits === 64 ? 4294967295 : (1 << highBits) - 1;
  const blockBytes = wordBytes * 2;
  const keyByteLength = wordBytes * config.keyWords;
  const keyLaneLength = config.keyWords * lanesPerWord;
  const roundKeyLaneLength = config.rounds * lanesPerWord;
  function round(xLo, xHi, yLo, yHi, keyLo, keyHi) {
    let rotated = rotrLong(xLo, xHi, config.alpha, highBits, highMask);
    rotated = addLong(rotated[0], rotated[1], yLo, yHi, highMask);
    xLo = (rotated[0] ^ keyLo) >>> 0;
    xHi = ((rotated[1] ^ keyHi) & highMask) >>> 0;
    rotated = rotlLong(yLo, yHi, config.beta, highBits, highMask);
    yLo = (rotated[0] ^ xLo) >>> 0;
    yHi = ((rotated[1] ^ xHi) & highMask) >>> 0;
    return [xLo, xHi, yLo, yHi];
  }
  function inverseRound(xLo, xHi, yLo, yHi, keyLo, keyHi) {
    let rotated = rotrLong((yLo ^ xLo) >>> 0, ((yHi ^ xHi) & highMask) >>> 0, config.beta, highBits, highMask);
    yLo = rotated[0];
    yHi = rotated[1];
    const subtracted = subLong((xLo ^ keyLo) >>> 0, ((xHi ^ keyHi) & highMask) >>> 0, yLo, yHi, highMask);
    rotated = rotlLong(subtracted[0], subtracted[1], config.alpha, highBits, highMask);
    xLo = rotated[0];
    xHi = rotated[1];
    return [xLo, xHi, yLo, yHi];
  }
  function assertBlockMultiple(bytes, name) {
    if (bytes.length % blockBytes !== 0) {
      throw new RangeError(`${name} length must be a multiple of ${blockBytes} bytes.`);
    }
  }
  function assertLaneLength(words, length, name) {
    if (words.length !== length) {
      throw new RangeError(`${name} must contain ${length} 32-bit lanes.`);
    }
  }
  function assertLaneBlockMultiple(words, name) {
    if (words.length % 4 !== 0) {
      throw new RangeError(`${name} length must be a multiple of 4 32-bit lanes.`);
    }
  }
  function assertRoundKeysAndDataWords(roundKeys, dataWords) {
    if (roundKeys.length !== roundKeyLaneLength) {
      throw new RangeError(`Round keys must contain ${roundKeyLaneLength} 32-bit lanes.`);
    }
    assertLaneBlockMultiple(dataWords, "Data");
  }
  function toWords(key, data) {
    const keyBytes = toByteArray(key);
    assertByteLength(keyBytes, keyByteLength, "Key");
    const dataBytes = toByteArray(data);
    assertBlockMultiple(dataBytes, "Data");
    return [toLongWordPairsLE(keyBytes, wordBytes), toLongWordPairsLE(dataBytes, wordBytes)];
  }
  function expandKey11(keyWords) {
    assertLaneLength(keyWords, keyLaneLength, "Key");
    const l = new WordArrayClass((config.rounds + config.keyWords - 2) * lanesPerWord);
    const roundKeys = new WordArrayClass(roundKeyLaneLength);
    roundKeys[0] = keyWords[0];
    roundKeys[1] = keyWords[1];
    for (let i = 1; i < config.keyWords; ++i) {
      const target = (i - 1) * lanesPerWord;
      const source = i * lanesPerWord;
      l[target] = keyWords[source];
      l[target + 1] = keyWords[source + 1];
    }
    for (let i = 0; i < config.rounds - 1; ++i) {
      const lOffset = i * lanesPerWord;
      const keyOffset = i * lanesPerWord;
      const result = round(l[lOffset], l[lOffset + 1], roundKeys[keyOffset], roundKeys[keyOffset + 1], i, 0);
      const nextLOffset = (i + config.keyWords - 1) * lanesPerWord;
      const nextKeyOffset = (i + 1) * lanesPerWord;
      l[nextLOffset] = result[0];
      l[nextLOffset + 1] = result[1];
      roundKeys[nextKeyOffset] = result[2];
      roundKeys[nextKeyOffset + 1] = result[3];
    }
    return roundKeys;
  }
  function encrypt11(key, data) {
    const [keyWords, dataWords] = toWords(key, data);
    return fromLongWordPairsLE(encryptWords11(keyWords, dataWords), wordBytes);
  }
  function encryptWords11(keyWords, dataWords) {
    return encryptWordsWithRoundKey11(expandKey11(keyWords), dataWords);
  }
  function encryptWordsWithRoundKey11(roundKeys, dataWords) {
    assertRoundKeysAndDataWords(roundKeys, dataWords);
    const result = new WordArrayClass(dataWords.length);
    for (let i = 0; i < dataWords.length; i += 4) {
      let yLo = dataWords[i];
      let yHi = dataWords[i + 1];
      let xLo = dataWords[i + 2];
      let xHi = dataWords[i + 3];
      for (let j = 0; j < config.rounds; ++j) {
        const keyOffset = j * lanesPerWord;
        const r = round(xLo, xHi, yLo, yHi, roundKeys[keyOffset], roundKeys[keyOffset + 1]);
        xLo = r[0];
        xHi = r[1];
        yLo = r[2];
        yHi = r[3];
      }
      result[i] = yLo;
      result[i + 1] = yHi;
      result[i + 2] = xLo;
      result[i + 3] = xHi;
    }
    return result;
  }
  function decrypt11(key, data) {
    const [keyWords, dataWords] = toWords(key, data);
    return fromLongWordPairsLE(decryptWords11(keyWords, dataWords), wordBytes);
  }
  function decryptWords11(keyWords, dataWords) {
    return decryptWordsWithRoundKey11(expandKey11(keyWords), dataWords);
  }
  function decryptWordsWithRoundKey11(roundKeys, dataWords) {
    assertRoundKeysAndDataWords(roundKeys, dataWords);
    const result = new WordArrayClass(dataWords.length);
    for (let i = 0; i < dataWords.length; i += 4) {
      let yLo = dataWords[i];
      let yHi = dataWords[i + 1];
      let xLo = dataWords[i + 2];
      let xHi = dataWords[i + 3];
      for (let j = config.rounds - 1; j >= 0; --j) {
        const keyOffset = j * lanesPerWord;
        const r = inverseRound(xLo, xHi, yLo, yHi, roundKeys[keyOffset], roundKeys[keyOffset + 1]);
        xLo = r[0];
        xHi = r[1];
        yLo = r[2];
        yHi = r[3];
      }
      result[i] = yLo;
      result[i + 1] = yHi;
      result[i + 2] = xLo;
      result[i + 3] = xHi;
    }
    return result;
  }
  return {
    blockBytes,
    keyBytes: keyByteLength,
    rounds: config.rounds,
    expandKey: expandKey11,
    encrypt: encrypt11,
    decrypt: decrypt11,
    encryptWords: encryptWords11,
    decryptWords: decryptWords11,
    encryptWordsWithRoundKey: encryptWordsWithRoundKey11,
    decryptWordsWithRoundKey: decryptWordsWithRoundKey11
  };
}

// src/speck96-96.ts
var speck96_96Block = createSpeckLongVariant({
  wordBits: 48,
  keyWords: 2,
  rounds: 28,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey6,
  encrypt: encrypt6,
  decrypt: decrypt6,
  encryptWords: encryptWords6,
  decryptWords: decryptWords6,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey6,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey6
} = speck96_96Block;

// src/speck96-144.ts
var speck96_144Block = createSpeckLongVariant({
  wordBits: 48,
  keyWords: 3,
  rounds: 29,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey7,
  encrypt: encrypt7,
  decrypt: decrypt7,
  encryptWords: encryptWords7,
  decryptWords: decryptWords7,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey7,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey7
} = speck96_144Block;

// src/speck128-128.ts
var speck128_128Block = createSpeckLongVariant({
  wordBits: 64,
  keyWords: 2,
  rounds: 32,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey8,
  encrypt: encrypt8,
  decrypt: decrypt8,
  encryptWords: encryptWords8,
  decryptWords: decryptWords8,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey8,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey8
} = speck128_128Block;

// src/speck128-192.ts
var speck128_192Block = createSpeckLongVariant({
  wordBits: 64,
  keyWords: 3,
  rounds: 33,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey9,
  encrypt: encrypt9,
  decrypt: decrypt9,
  encryptWords: encryptWords9,
  decryptWords: decryptWords9,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey9,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey9
} = speck128_192Block;

// src/speck128-256.ts
var speck128_256Block = createSpeckLongVariant({
  wordBits: 64,
  keyWords: 4,
  rounds: 34,
  alpha: 8,
  beta: 3
});
var {
  expandKey: expandKey10,
  encrypt: encrypt10,
  decrypt: decrypt10,
  encryptWords: encryptWords10,
  decryptWords: decryptWords10,
  encryptWordsWithRoundKey: encryptWordsWithRoundKey10,
  decryptWordsWithRoundKey: decryptWordsWithRoundKey10
} = speck128_256Block;

// src/index.ts
var speck32_64$1 = speck32_64Block;
var speck48_72$1 = speck48_72Block;
var speck48_96$1 = speck48_96Block;
var speck64_96$1 = speck64_96Block;
var speck64_128$1 = speck64_128Block;
var speck96_96$1 = speck96_96Block;
var speck96_144$1 = speck96_144Block;
var speck128_128$1 = speck128_128Block;
var speck128_192$1 = speck128_192Block;
var speck128_256$1 = speck128_256Block;

const variants = {
    '32-64': speck32_64$1,
    '48-72': speck48_72$1,
    '48-96': speck48_96$1,
    '64-96': speck64_96$1,
    '64-128': speck64_128$1,
    '96-96': speck96_96$1,
    '96-144': speck96_144$1,
    '128-128': speck128_128$1,
    '128-192': speck128_192$1,
    '128-256': speck128_256$1,
};
function createSpeckCipher(variant, key) {
    if (!variant) {
        throw new Error('SPECK variant is required.');
    }
    const impl = variants[variant];
    if (!impl) {
        throw new Error(`Unknown SPECK variant: ${String(variant)}.`);
    }
    if (key.length !== impl.keyBytes) {
        throw new Error(`SPECK ${variant} key must be ${impl.keyBytes * 8} bits.`);
    }
    const blockSize = impl.blockBytes;
    return {
        blockSize,
        encryptBlock(input, inputOffset, output, outputOffset) {
            transformBlock(impl, key, input, inputOffset, output, outputOffset, true);
        },
        decryptBlock(input, inputOffset, output, outputOffset) {
            transformBlock(impl, key, input, inputOffset, output, outputOffset, false);
        },
        encrypt(input, output) {
            return transformBlocks(impl, key, input, output, true);
        },
        decrypt(input, output) {
            return transformBlocks(impl, key, input, output, false);
        },
    };
}
function createComponent(name, variant) {
    const impl = variants[variant];
    return {
        kind: 'cipher',
        name,
        type: 'block',
        blockSize: impl.blockBytes,
        keySizes: [impl.keyBytes],
        create(key) {
            return createSpeckCipher(variant, key);
        },
    };
}
const speck32_64 = createComponent('SPECK32/64', '32-64');
const speck48_72 = createComponent('SPECK48/72', '48-72');
const speck48_96 = createComponent('SPECK48/96', '48-96');
const speck64_96 = createComponent('SPECK64/96', '64-96');
const speck64_128 = createComponent('SPECK64/128', '64-128');
const speck96_96 = createComponent('SPECK96/96', '96-96');
const speck96_144 = createComponent('SPECK96/144', '96-144');
const speck128_128 = createComponent('SPECK128/128', '128-128');
const speck128_192 = createComponent('SPECK128/192', '128-192');
const speck128_256 = createComponent('SPECK128/256', '128-256');
const allSpeckComponents = [
    speck32_64,
    speck48_72,
    speck48_96,
    speck64_96,
    speck64_128,
    speck96_96,
    speck96_144,
    speck128_128,
    speck128_192,
    speck128_256,
];
const speckPreset = {
    kind: 'preset',
    name: 'speck',
    components() {
        return allSpeckComponents;
    },
};
function transformBlocks(impl, key, input, output, encrypting) {
    assertBlocks$1(input, output, impl.blockBytes);
    for (let offset = 0; offset < input.length; offset += impl.blockBytes) {
        transformBlock(impl, key, input, offset, output, offset, encrypting);
    }
    return output;
}
function transformBlock(impl, key, input, inputOffset, output, outputOffset, encrypting) {
    const block = input.subarray(inputOffset, inputOffset + impl.blockBytes);
    if (block.length !== impl.blockBytes) {
        throw new Error(`SPECK block must be ${impl.blockBytes * 8} bits.`);
    }
    const transformed = encrypting ? impl.encrypt(key, block) : impl.decrypt(key, block);
    output.set(transformed, outputOffset);
}
function assertBlocks$1(input, output, blockSize) {
    if (input.length % blockSize !== 0) {
        throw new Error(`SPECK input length must be a multiple of ${blockSize * 8} bits.`);
    }
    if (output.length !== input.length) {
        throw new Error('SPECK output length must equal input length.');
    }
}

function encryptWithBlockOffset(cipher, input, inputOffset, output, outputOffset) {
    if (cipher.encryptBlock) {
        cipher.encryptBlock(input, inputOffset, output, outputOffset);
        return;
    }
    cipher.encrypt(input.subarray(inputOffset, inputOffset + cipher.blockSize), output.subarray(outputOffset, outputOffset + cipher.blockSize));
}
function decryptWithBlockOffset(cipher, input, inputOffset, output, outputOffset) {
    if (cipher.decryptBlock) {
        cipher.decryptBlock(input, inputOffset, output, outputOffset);
        return;
    }
    cipher.decrypt(input.subarray(inputOffset, inputOffset + cipher.blockSize), output.subarray(outputOffset, outputOffset + cipher.blockSize));
}

const tripleDes = {
    kind: 'cipher',
    name: 'TripleDES',
    type: 'block',
    blockSize: 8,
    keySizes: [16, 24],
    create(key) {
        return createTripleDesCipher(key);
    },
};
const tripleDesPreset = {
    kind: 'preset',
    name: 'triple-des',
    components() {
        return [tripleDes];
    },
};
function createTripleDesCipher(key) {
    if (key.length !== 16 && key.length !== 24) {
        throw new Error('Triple DES key must be 128 or 192 bits.');
    }
    const first = createDesCipher(key.subarray(0, 8));
    const second = createDesCipher(key.subarray(8, 16));
    const third = createDesCipher(key.length === 24 ? key.subarray(16, 24) : key.subarray(0, 8));
    const firstOutput = new Uint8Array(8);
    const secondOutput = new Uint8Array(8);
    return {
        blockSize: 8,
        encryptBlock(input, inputOffset, output, outputOffset) {
            encryptTripleBlockAt(input, inputOffset, output, outputOffset, first, second, third, firstOutput, secondOutput);
        },
        decryptBlock(input, inputOffset, output, outputOffset) {
            decryptTripleBlockAt(input, inputOffset, output, outputOffset, first, second, third, firstOutput, secondOutput);
        },
        encrypt(input, output) {
            assertBlocks(input, output);
            for (let offset = 0; offset < input.length; offset += 8) {
                encryptTripleBlockAt(input, offset, output, offset, first, second, third, firstOutput, secondOutput);
            }
            return output;
        },
        decrypt(input, output) {
            assertBlocks(input, output);
            for (let offset = 0; offset < input.length; offset += 8) {
                decryptTripleBlockAt(input, offset, output, offset, first, second, third, firstOutput, secondOutput);
            }
            return output;
        },
    };
}
function encryptTripleBlockAt(input, inputOffset, output, outputOffset, first, second, third, firstOutput, secondOutput) {
    encryptWithBlockOffset(first, input, inputOffset, firstOutput, 0);
    decryptWithBlockOffset(second, firstOutput, 0, secondOutput, 0);
    encryptWithBlockOffset(third, secondOutput, 0, output, outputOffset);
}
function decryptTripleBlockAt(input, inputOffset, output, outputOffset, first, second, third, thirdOutput, secondOutput) {
    decryptWithBlockOffset(third, input, inputOffset, thirdOutput, 0);
    encryptWithBlockOffset(second, thirdOutput, 0, secondOutput, 0);
    decryptWithBlockOffset(first, secondOutput, 0, output, outputOffset);
}
function assertBlocks(input, output) {
    if (input.length % 8 !== 0) {
        throw new Error('Triple DES input length must be a multiple of 64 bits.');
    }
    if (output.length !== input.length) {
        throw new Error('Triple DES output length must equal input length.');
    }
}

function classicCiphersPreset() {
    return {
        kind: 'preset',
        name: 'classic-ciphers',
        components() {
            return [aesPreset, desPreset, rc4Preset, tripleDesPreset];
        },
    };
}
function ciphersPreset() {
    return {
        kind: 'preset',
        name: 'ciphers',
        components() {
            return [classicCiphersPreset(), speckPreset, chacha20Preset];
        },
    };
}

export { aes, aesCcm, aesGcm, aesPreset, allChaCha20Components, allSpeckComponents, chacha20, chacha20Poly1305, chacha20Preset, ciphersPreset, classicCiphersPreset, createAesCcmComponent, createAesCipher, createDesCipher, createRc4Transform, createSpeckCipher, createTripleDesCipher, des, desPreset, rc4, rc4Drop, rc4Preset, speck128_128, speck128_192, speck128_256, speck32_64, speck48_72, speck48_96, speck64_128, speck64_96, speck96_144, speck96_96, speckPreset, tripleDes, tripleDesPreset, xchacha20, xchacha20Poly1305 };
//# sourceMappingURL=index.mjs.map
